import React, { memo, startTransition, useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantTheme } from '../../contexts/TenantThemeContext';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../../services/api';
import { getStoredSessionToken } from '../../services/sessionTokens';
import liveChatApi from '../../services/liveChatApi';
import { toast } from 'sonner';
import {
  Wallet, LogOut, Copy, ArrowUpCircle, ArrowDownCircle,
  CheckCircle2, XCircle, MessageCircle, Send, Search, Sparkles,
  Crown, RefreshCw, Star, KeyRound, Gamepad2, Mail, Lock, Home,
  ChevronDown, ChevronUp, MessagesSquare, Facebook, ChevronLeft,
  BadgeCheck, Pencil, X, CircleUser, Camera, Check, Gift, CircleDollarSign,
  Trophy, Bell, Megaphone, Gem, CalendarDays, Rocket, Headphones, Settings,
  Flame, ChevronRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle
} from '../../components/ui/sheet';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import {
  Popover, PopoverContent, PopoverTrigger
} from '../../components/ui/popover';
import useNotificationsWS from '../../hooks/useNotificationsWS';

// Extracted player-app sub-components — see ./components/* for each.
import AddGamePanel from './components/AddGamePanel';
import CustomGameForm from './components/CustomGameForm';
import { FacebookGlyph, DiscordGlyph, TelegramGlyph, InstagramGlyph, YoutubeGlyph, TikTokGlyph } from './components/Glyphs';
import { tap } from './components/playerHelpers';
import MyGamesSection from './components/MyGames';
import WalletFlow, { FullScreenWhitePanel, Stepper, Row, PaymentMethodLogo } from './components/WalletFlow';
import { ResetPasswordPanel, EditProfilePanel } from './components/ProfilePanels';
import { AnnouncementsPanel, NotificationsPanel, AchievementsPanel } from './components/SidePanels';
import ProfileFullScreen from './components/ProfileFullScreen';
import PromoBanners from './components/PromoBanners';
import BalanceDetailPanel from './components/BalanceDetailPanel';
import ChatPanel from './components/ChatPanel';
import SupportMenu from './components/SupportMenu';
import SafeImage from '../../components/SafeImage';
import referAndEarnGiftCutout from '../../assets/refer-and-earn-gift-cutout.png';

const PENDING_GAMES_STORAGE_KEY = 'vl_pending_game_accounts';
const FAVORITES_STORAGE_KEY = 'vl_favs';
const PUSH_NOTIFICATIONS_STORAGE_KEY = 'vl_push_notifications_enabled_at';
const PUSH_SW_URL = '/sw.js';
const PLAYER_HISTORY_GUARD_DEPTH = 4;
const PLAYER_DATA_MIN_LOADING_MS = 700;
const GAME_CREATE_CARD_TIMEOUT_MS = 2 * 60 * 1000;
const BRANDING_REFRESH_INTERVAL_MS = 12000;
const PLAYER_UI_ACCENT_BORDER = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #004D61)) 48%, transparent)';
const PLAYER_UI_ACCENT_GLOW = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #004D61)) 28%, transparent)';
const HALF_SHEET_DIALOG_STYLE = {
  background: 'var(--vl-bg, #000000)',
  borderColor: PLAYER_UI_ACCENT_BORDER,
  boxShadow: `0 -18px 48px rgba(0,0,0,0.58), 0 0 34px ${PLAYER_UI_ACCENT_GLOW}`,
  color: '#FFFFFF',
  position: 'fixed',
  left: '50%',
  right: 'auto',
  top: 'auto',
  bottom: 0,
  transform: 'translateX(-50%)',
  width: 'min(100vw, 520px)',
  maxWidth: '520px',
  maxHeight: '56dvh',
  overflowY: 'auto',
  borderRadius: '28px 28px 0 0',
};

const emptyPlayerData = () => ({ games: [], methods: [], transactions: [], accounts: [] });

const scheduleIdleWork = (callback, { timeout = 500 } = {}) => {
  if (typeof window === 'undefined') return null;
  if (typeof window.requestIdleCallback === 'function') {
    return { type: 'idle', id: window.requestIdleCallback(callback, { timeout }) };
  }
  return { type: 'timeout', id: window.setTimeout(callback, 16) };
};

const cancelIdleWork = (handle) => {
  if (!handle || typeof window === 'undefined') return;
  if (handle.type === 'idle' && typeof window.cancelIdleCallback === 'function') {
    window.cancelIdleCallback(handle.id);
    return;
  }
  window.clearTimeout(handle.id);
};

const sameUnreadAgents = (a = [], b = []) => {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  return a.every((item, index) => {
    const other = b[index] || {};
    return String(item?.agent_id || '') === String(other?.agent_id || '')
      && String(item?.agent_name || '') === String(other?.agent_name || '')
      && Number(item?.unread_count || 0) === Number(other?.unread_count || 0)
      && String(item?.last_message || '') === String(other?.last_message || '');
  });
};

const scopedPlayerStorageKey = (base, tenantSlug, userId) => (
  userId ? `${base}:${tenantSlug || 'global'}:${userId}` : base
);

const isLoopbackHost = () => {
  if (typeof window === 'undefined') return false;
  return ['localhost', '127.0.0.1'].includes(window.location.hostname);
};

const isLocalDemoPreview = () => {
  if (typeof window === 'undefined') return false;
  return isLoopbackHost() && /^\/demo(\/|$)/.test(window.location.pathname || '');
};

const canUsePlayerPushNotifications = () => (
  typeof window !== 'undefined' &&
  'Notification' in window &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  (window.isSecureContext || isLoopbackHost())
);

const getPlayerNotificationPermission = () => {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission || 'default';
};

const urlBase64ToUint8Array = (base64String = '') => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = `${base64String}${padding}`.replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

const registerPlayerPushServiceWorker = async () => {
  if (!canUsePlayerPushNotifications()) return null;
  const existing = await navigator.serviceWorker.getRegistration('/');
  return existing || navigator.serviceWorker.register(PUSH_SW_URL, { scope: '/' });
};

const enablePlayerPushNotifications = async () => {
  if (!canUsePlayerPushNotifications()) return { ok: false, status: 'unsupported' };

  let permission = getPlayerNotificationPermission();
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') {
    return { ok: false, status: permission || 'denied' };
  }

  const registration = await registerPlayerPushServiceWorker();
  if (!registration?.pushManager) return { ok: false, status: 'unsupported' };

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    let data;
    try {
      ({ data } = await api.get('/api/push/vapid-key'));
    } catch {
      return { ok: false, status: 'sync-unavailable' };
    }
    const publicKey = String(data?.public_key || '').trim();
    if (!publicKey) return { ok: false, status: 'missing-key' };
    try {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    } catch {
      return { ok: false, status: 'subscribe-failed' };
    }
  }

  const json = subscription.toJSON();
  try {
    await api.post('/api/push/subscribe', {
      endpoint: json.endpoint,
      keys: json.keys,
    });
  } catch {
    return { ok: false, status: 'save-failed' };
  }

  return { ok: true, status: 'granted' };
};

const setBrowserAppBadge = (count) => {
  if (typeof navigator === 'undefined') return;
  const nextCount = Math.max(0, Math.min(99, Number(count) || 0));
  try {
    if (nextCount > 0 && typeof navigator.setAppBadge === 'function') {
      void navigator.setAppBadge(nextCount);
    } else if (nextCount <= 0 && typeof navigator.clearAppBadge === 'function') {
      void navigator.clearAppBadge();
    }
  } catch { /* noop */ }

  try {
    navigator.serviceWorker?.controller?.postMessage({
      type: nextCount > 0 ? 'SET_APP_BADGE' : 'CLEAR_APP_BADGE',
      count: nextCount,
    });
  } catch { /* noop */ }
};

const chatAgentKey = (agent) => {
  const id = String(agent?.id || '').trim().toLowerCase();
  if (id) return `id:${id}`;
  const name = String(agent?.name || '').trim().toLowerCase();
  return name ? `name:${name}` : '';
};

const readStoredArray = (storage, key) => {
  try {
    if (!storage || !key) return [];
    const parsed = JSON.parse(storage.getItem(key) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const freshGet = (url, scope) => api.get(url, {
  params: {
    _scope: scope || 'player',
    _ts: Date.now(),
  },
  headers: {
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
  },
});

const finiteNumberOr = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const paymentMethodDisplayCode = (method) => String(
  method?.gateway_method_code
  || method?.gateway_method
  || method?.method
  || method?.code
  || method?.title
  || ''
).toLowerCase().replace(/[^a-z0-9]+/g, '');

const defaultPaymentMethodOrder = (method) => {
  const code = paymentMethodDisplayCode(method);
  if (code === 'cashapp') return 10;
  if (code === 'applepay') return 20;
  if (code === 'googlepay') return 30;
  if (code === 'chime') return 40;
  if (code === 'venmo') return 50;
  return 500;
};

const paymentMethodOrder = (method) => {
  const raw = Number(method?.display_order ?? method?.sort_order ?? method?.order);
  return Number.isFinite(raw) ? raw : defaultPaymentMethodOrder(method);
};

const INSTALL_SETUP_TENANT_MATCHERS = ['jenny', 'fortune'];
const GENERIC_PLAYER_BRAND_NAMES = new Set(['mystic reels', 'teamlobby', 'playemp']);

const cleanBrandLabel = (value) => String(value || '').trim();

const isGenericPlayerBrandName = (value) => {
  const cleaned = cleanBrandLabel(value).toLowerCase();
  return !cleaned || GENERIC_PLAYER_BRAND_NAMES.has(cleaned);
};

const shouldShowInstallSetupNudges = ({ tenantSlug, branding, user }) => {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const haystack = [
    tenantSlug,
    branding?.brand_name,
    branding?.brand_tagline,
    user?.tenant_name,
    host,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return INSTALL_SETUP_TENANT_MATCHERS.some((matcher) => haystack.includes(matcher));
};

function AllowNotificationsNudge({ tenantSlug, userId }) {
  const storageKey = scopedPlayerStorageKey(PUSH_NOTIFICATIONS_STORAGE_KEY, tenantSlug, userId);
  const [visible, setVisible] = useState(false);
  const [permission, setPermission] = useState(getPlayerNotificationPermission());
  const [busy, setBusy] = useState(false);
  const [syncStatus, setSyncStatus] = useState('');

  useEffect(() => {
    const refresh = () => {
      const nextPermission = getPlayerNotificationPermission();
      setPermission(nextPermission);
      if (nextPermission === 'granted') setSyncStatus((status) => status || 'allowed');
      const synced = (() => {
        try { return Boolean(localStorage.getItem(storageKey)); } catch { return false; }
      })();
      setVisible(!synced);
    };

    refresh();
    const onReady = () => {
      setPermission('granted');
      setVisible(false);
    };
    window.addEventListener('vl:push-enabled', onReady);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('vl:push-enabled', onReady);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [storageKey]);

  if (!visible) return null;

  const handleAllow = async () => {
    tap();
    setBusy(true);
    try {
      const result = await enablePlayerPushNotifications();
      const nextPermission = getPlayerNotificationPermission();
      setPermission(nextPermission);
      const finishLocalSync = (description = 'App alerts are ready for this local preview.') => {
        try { localStorage.setItem(storageKey, String(Date.now())); } catch { /* noop */ }
        try { window.dispatchEvent(new Event('vl:push-enabled')); } catch { /* noop */ }
        toast.success('Notifications enabled', {
          description,
          duration: 2400,
        });
      };
      if (result.ok) {
        finishLocalSync('App alerts and badge counts are ready for this device.');
        return;
      }

      if (result.status === 'denied') {
        setPermission('denied');
        toast.error('Still blocked', {
          description: 'Open browser site settings, set Notifications to Allow, then tap Sync.',
          duration: 3200,
        });
        return;
      }
      if (result.status === 'missing-key') {
        if (nextPermission === 'granted' && isLocalDemoPreview()) {
          finishLocalSync('Permission is on for local demo. Live will save the device subscription.');
          return;
        }
        setSyncStatus('setup-missing');
        toast.error('Notification sync not ready', {
          description: 'The push key is missing on this environment. Try Sync on the live app.',
          duration: 3000,
        });
        return;
      }
      if (result.status === 'unsupported') {
        setSyncStatus('unsupported');
        toast.error('Notifications are not supported here', {
          description: 'Open in Chrome or the installed app, then tap Allow again.',
          duration: 3200,
        });
        return;
      }
      if (['sync-unavailable', 'save-failed'].includes(result.status)) {
        if (nextPermission === 'granted' && isLocalDemoPreview()) {
          finishLocalSync('Permission is on for local demo. Live will save the device subscription.');
          return;
        }
        setSyncStatus('sync-failed');
        toast.error('Permission allowed, sync not finished', {
          description: 'This device could not be saved yet. Tap Sync again when the app is online.',
          duration: 3600,
        });
        return;
      }
      if (result.status === 'subscribe-failed') {
        if (nextPermission === 'granted' && isLocalDemoPreview()) {
          finishLocalSync('Permission is on for local demo. Live will save the device subscription.');
          return;
        }
        setSyncStatus('subscribe-failed');
        toast.error('Permission allowed, sync not finished', {
          description: 'Reload or install the app, then tap Sync again.',
          duration: 3600,
        });
        return;
      }
      toast.error('Could not enable notifications', {
        description: 'Try again after installing the app or reloading.',
        duration: 3000,
      });
    } catch {
      setSyncStatus('sync-failed');
      toast.error('Could not enable notifications', {
        description: 'Permission may be allowed, but device sync did not finish. Tap Sync again.',
        duration: 3000,
      });
    } finally {
      setBusy(false);
    }
  };

  const isBlocked = permission === 'denied';
  const isAllowedPending = permission === 'granted';
  const ctaLabel = busy ? 'Checking' : isBlocked || isAllowedPending ? 'Sync' : 'Allow';
  const kickerLabel = isBlocked
    ? 'Notifications blocked'
    : isAllowedPending
      ? 'Notifications allowed'
      : 'Allow notifications';
  const bodyCopy = isBlocked
    ? 'Turn notifications on in browser settings, then tap Sync.'
    : isAllowedPending
      ? syncStatus === 'sync-failed' || syncStatus === 'setup-missing' || syncStatus === 'subscribe-failed'
        ? 'Permission is on. Tap Sync again to save this device.'
        : 'Permission is on. Tap Sync to finish this device setup.'
      : 'Get bonus, payment, and chat alerts in the installed app.';

  return (
    <section
      className="overflow-hidden rounded-[18px] border px-3 py-2.5 sm:px-3.5"
      style={{
        background: 'linear-gradient(135deg, rgba(255,61,113,0.15), rgba(0,107,133,0.11) 48%, rgba(34,211,238,0.12))',
        borderColor: 'rgba(255,255,255,0.16)',
        boxShadow: '0 14px 32px rgba(0,0,0,0.26), inset 0 1px 0 rgba(255,255,255,0.08)',
      }}
      data-testid="allow-notifications-nudge"
    >
      <div className="flex items-center gap-2.5">
        <div
          className="relative grid h-10 w-10 shrink-0 place-items-center rounded-xl"
          style={{
            background: 'rgba(0,0,0,0.34)',
            border: '1px solid rgba(255,255,255,0.14)',
            boxShadow: 'inset 0 0 16px rgba(255,255,255,0.05)',
          }}
          aria-hidden
        >
          <Bell className="h-[18px] w-[18px] text-white" />
          <span
            className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full px-1 text-[9px] font-black text-white"
            style={{ background: '#FF3D71', boxShadow: '0 6px 14px rgba(255,61,113,0.34)' }}
          >
            1
          </span>
        </div>

        <button
          type="button"
          onClick={handleAllow}
          disabled={busy}
          className="min-w-0 flex-1 text-left disabled:cursor-wait disabled:opacity-80"
          data-testid="allow-notifications-nudge-button"
        >
          <p className="text-[9px] font-black uppercase tracking-[0.15em]" style={{ color: '#FF8BA7' }}>
            {kickerLabel}
          </p>
          <p className="display mt-0.5 text-sm font-extrabold leading-tight text-white sm:text-base">
            Never miss an update
          </p>
          <p className="mt-0.5 text-[11px] leading-snug text-white/68 sm:text-xs">
            {bodyCopy}
          </p>
        </button>

        <button
          type="button"
          onClick={handleAllow}
          disabled={busy}
          className="shrink-0 rounded-xl px-3 py-1.5 text-[11px] font-black text-white transition active:scale-[0.97] disabled:cursor-wait disabled:opacity-80 sm:px-3.5"
          style={{
            background: 'linear-gradient(135deg, #FF3D71, #006B85)',
            boxShadow: '0 10px 20px rgba(255,61,113,0.18)',
          }}
          data-testid="allow-notifications-nudge-cta"
        >
          {ctaLabel}
        </button>
      </div>
    </section>
  );
}

const sortPlayerPaymentMethods = (items = []) => [...items].sort((a, b) => {
  const orderDiff = paymentMethodOrder(a) - paymentMethodOrder(b);
  if (orderDiff !== 0) return orderDiff;
  return String(a?.title || a?.method || '').localeCompare(String(b?.title || b?.method || ''));
});

const accountSyncTime = (account) => {
  const value = account?.last_balance_synced_at || account?.last_synced_at || account?.updated_at || account?.created_at;
  const time = Date.parse(value || '');
  return Number.isFinite(time) ? time : 0;
};

const sameAccount = (a, b) => {
  if (!a || !b) return false;
  if (a.id && b.id && String(a.id) === String(b.id)) return true;
  if (a.game_id && b.game_id && String(a.game_id) === String(b.game_id)) return true;
  if (a.game_code && b.game_code && String(a.game_code) === String(b.game_code)) return true;
  return false;
};

const mergeLiveBalanceRows = (accounts, rows) => {
  const liveRows = Array.isArray(rows) ? rows : [];
  if (!liveRows.length) return Array.isArray(accounts) ? accounts : [];
  return (Array.isArray(accounts) ? accounts : []).map(account => {
    const row = liveRows.find(item => sameAccount(account, item));
    if (!row) return account;
    const rowTime = accountSyncTime(row);
    const currentTime = accountSyncTime(account);
    if (rowTime && currentTime && rowTime < currentTime) return account;
    const liveBalance = finiteNumberOr(row.balance_usd ?? row.last_known_balance ?? row.balance, account.last_known_balance ?? account.balance_usd ?? 0);
    const syncedAt = row.last_balance_synced_at || row.last_synced_at || new Date().toISOString();
    return {
      ...account,
      last_known_balance: liveBalance,
      balance_usd: liveBalance,
      balance_sc: finiteNumberOr(row.balance_sc, liveBalance),
      provider_status: row.provider_status || account.provider_status,
      is_cached: Boolean(row.is_cached),
      last_balance_synced_at: syncedAt,
      last_load_amount: row.last_load_amount ?? account.last_load_amount,
      last_load_fund_source: row.last_load_fund_source ?? account.last_load_fund_source,
      last_load_at: row.last_load_at ?? account.last_load_at,
      last_redeem_at: row.last_redeem_at ?? account.last_redeem_at,
    };
  });
};

const mergeServerAccountsPreservingLive = (existingAccounts, serverAccounts) => {
  const existing = Array.isArray(existingAccounts) ? existingAccounts : [];
  const incoming = Array.isArray(serverAccounts) ? serverAccounts : [];
  const merged = existing.flatMap(current => {
    const account = incoming.find(item => sameAccount(item, current));
    if (!account) {
      // A successful server response is authoritative for saved accounts.
      // Keep only an in-flight placeholder; a formerly successful local pin
      // must disappear when an admin deletes its server record.
      return current.__creating ? [current] : [];
    }
    const currentTime = accountSyncTime(current);
    const incomingTime = accountSyncTime(account);
    if (currentTime > incomingTime) {
      return [{
        ...current,
        ...account,
        last_known_balance: current.last_known_balance ?? current.balance_usd ?? account.last_known_balance,
        balance_usd: current.balance_usd ?? current.last_known_balance ?? account.balance_usd,
        balance_sc: current.balance_sc ?? account.balance_sc,
        provider_status: current.provider_status || account.provider_status,
        is_cached: current.is_cached ?? account.is_cached,
        last_balance_synced_at: current.last_balance_synced_at || account.last_balance_synced_at,
      }];
    }
    return [{
      ...current,
      ...account,
      __localOptimistic: current.__localOptimistic || account.__localOptimistic,
      __localPinnedAt: current.__localPinnedAt || account.__localPinnedAt,
    }];
  });

  const newIncoming = incoming.filter(account => (
    !existing.some(item => sameAccount(item, account))
  ));

  const stillPending = existing.filter(account => (
    account.__creating &&
    !merged.some(item => sameAccount(item, account))
  ));
  return [...stillPending, ...merged, ...newIncoming];
};

const compactAlertTime = (value) => {
  const time = Date.parse(value || '');
  if (!Number.isFinite(time)) return '';
  const diffMs = Date.now() - time;
  const minute = 60 * 1000;
  const hour = 60 * minute;
  if (diffMs < minute) return 'now';
  if (diffMs < hour) return `${Math.max(1, Math.floor(diffMs / minute))}m ago`;
  if (diffMs < 24 * hour) return `${Math.floor(diffMs / hour)}h ago`;
  return new Date(time).toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const isNotificationUnread = (item) => !item?.read && !item?.is_read;

function HeaderAlertsButton({
  unread,
  open,
  onOpenChange,
  loading,
  items,
  onItemClick,
  onMarkAll,
  onViewAll,
}) {
  const latest = (items || []).slice(0, 5);
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="tap-pop-alert relative grid h-12 w-12 shrink-0 place-items-center rounded-full transition active:scale-95 hover:brightness-110"
          style={{
            background: 'radial-gradient(circle at 32% 24%, rgba(255,255,255,0.30) 0%, rgba(255,217,61,0.22) 32%, rgba(0,107,133,0.16) 72%, rgba(10,18,26,0.86) 100%)',
            border: '1px solid rgba(255,217,61,0.42)',
            boxShadow: '0 10px 24px rgba(0,0,0,0.30), 0 0 18px rgba(255,217,61,0.22), inset 0 0 10px rgba(255,255,255,0.16)',
          }}
          aria-label="Open alerts"
          data-testid="header-alerts-button"
        >
          <Bell className="h-5 w-5 text-white" strokeWidth={2.6} />
          {unread > 0 && (
            <span
              className="absolute -bottom-1.5 -left-1.5 min-w-[22px] rounded-full px-1.5 py-0.5 text-center text-[10px] font-black leading-none text-white"
              style={{
                background: '#FF3D71',
                border: '2px solid var(--vl-bg, #000)',
                boxShadow: '0 0 14px rgba(255,61,113,0.76)',
              }}
              data-testid="header-alerts-badge"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={12}
        className="overflow-hidden rounded-3xl border p-0 shadow-2xl"
        style={{
          width: 'min(330px, calc(100vw - 24px))',
          background: 'linear-gradient(180deg, rgba(15,21,32,0.98), rgba(5,9,14,0.98))',
          borderColor: 'rgba(224,244,247,0.16)',
          color: '#fff',
          boxShadow: '0 22px 60px rgba(0,0,0,0.55), 0 0 28px rgba(0,77,97,0.22), inset 0 1px 0 rgba(255,255,255,0.08)',
        }}
        data-testid="header-alerts-popover"
      >
        <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
          <div>
            <p className="display text-sm font-extrabold text-white">Alerts</p>
            <p className="text-[11px] text-white/55">Latest 5 updates</p>
          </div>
          {unread > 0 && (
            <button
              type="button"
              onClick={onMarkAll}
              className="rounded-full px-3 py-1 text-[11px] font-bold text-white"
              style={{ background: 'rgba(0,77,97,0.32)', border: '1px solid rgba(224,244,247,0.18)' }}
              data-testid="header-alerts-mark-all"
            >
              Mark read
            </button>
          )}
        </div>
        <div className="max-h-[360px] overflow-y-auto p-2">
          {loading && (
            <div className="py-8 text-center text-sm text-white/60">Loading alerts...</div>
          )}
          {!loading && latest.length === 0 && (
            <div className="py-8 text-center">
              <Bell className="mx-auto mb-2 h-8 w-8 text-white/40" />
              <p className="text-sm font-bold text-white/75">No alerts yet</p>
            </div>
          )}
          {!loading && latest.map(item => {
            const unreadItem = isNotificationUnread(item);
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onItemClick(item)}
                className="w-full rounded-2xl px-3 py-2.5 text-left transition active:scale-[0.99] hover:bg-white/5"
                style={{
                  background: unreadItem ? 'rgba(0,77,97,0.22)' : 'rgba(255,255,255,0.035)',
                  border: `1px solid ${unreadItem ? 'rgba(224,244,247,0.16)' : 'rgba(255,255,255,0.07)'}`,
                }}
                data-testid={`header-alert-${item.id}`}
              >
                <div className="flex items-start gap-2">
                  <Bell className="mt-0.5 h-4 w-4 shrink-0" style={{ color: unreadItem ? '#6FE8D9' : 'rgba(255,255,255,0.42)' }} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate text-[13px] font-extrabold text-white">{item.title || 'Notification'}</p>
                      <span className="shrink-0 text-[10px] text-white/42">{compactAlertTime(item.created_at)}</span>
                    </div>
                    {item.message && (
                      <p className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-white/62">{item.message}</p>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={onViewAll}
          className="w-full border-t border-white/10 px-4 py-3 text-center text-xs font-extrabold text-white transition hover:bg-white/5"
          data-testid="header-alerts-view-all"
        >
          Open all alerts
        </button>
      </PopoverContent>
    </Popover>
  );
}

const LiveWinsStrip = memo(function LiveWinsStrip({ items, paused = false }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!items.length) {
      setIndex(0);
      return undefined;
    }
    setIndex(current => current % items.length);
    if (paused) return undefined;
    const timer = setInterval(() => {
      setIndex(current => (current + 1) % items.length);
    }, 2800);
    return () => clearInterval(timer);
  }, [items.length, paused]);

  const activeItem = items[index % Math.max(items.length, 1)];
  if (!activeItem) return null;

  return (
    <div
      className="mb-2 flex h-9 items-center overflow-hidden rounded-full border px-3 text-white shadow-[0_10px_24px_rgba(0,0,0,0.26)]"
      style={{
        background: 'linear-gradient(90deg, rgba(4,8,17,0.78), rgba(21,12,35,0.66), rgba(4,8,17,0.78))',
        borderColor: 'rgba(245,188,75,0.36)',
        backdropFilter: 'blur(10px)',
      }}
      data-testid="home-live-wins-strip"
    >
      <span
        className="mr-2 shrink-0 rounded-full px-2 py-0.5 text-[9px] font-black uppercase text-black"
        style={{ background: 'linear-gradient(135deg, #FFE66D, #F5BC4B)', letterSpacing: '0.14em' }}
      >
        Live Wins
      </span>
      <div key={`${activeItem.player}-${activeItem.gameName}-${index}`} className="min-w-0 animate-[live-win-slide_2.8s_ease-in-out] truncate text-[12px] font-extrabold sm:text-sm">
        <span className="text-white">{activeItem.player}</span>
        <span className="text-white/70"> wins </span>
        <span className="text-[#FFE66D]">${activeItem.amount}</span>
        <span className="text-white/70"> on </span>
        <span className="text-white">{activeItem.gameName}</span>
      </div>
    </div>
  );
});

function RequiredNameOnboarding({ user, branding, onComplete }) {
  const brandName = cleanBrandLabel(branding?.brand_name) || cleanBrandLabel(user?.tenant_name) || 'your lobby';
  const brandLogo = branding?.logo_url || branding?.intro_logo_url || '';
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const cleanName = name.trim().replace(/\s+/g, ' ');
  const canSave = cleanName.length >= 2;

  const submit = async (event) => {
    event?.preventDefault?.();
    if (!canSave || saving) return;
    setSaving(true);
    try {
      const sessionToken = getStoredSessionToken({ portal: 'player', allowLegacyFallback: true })
        || getStoredSessionToken({ allowLegacyFallback: true });
      if (!sessionToken) throw new Error('SESSION_REQUIRED');
      await api.patch('/api/auth/profile', { full_name: cleanName }, {
        headers: { Authorization: `Bearer ${sessionToken}` },
      });
      toast.success('Name saved');
      await onComplete?.();
    } catch (error) {
      const message = error?.message === 'SESSION_REQUIRED' || error?.response?.status === 401
        ? 'Please sign in again to save your name.'
        : error?.response?.data?.detail || 'Could not save your name. Please try again.';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center px-4 py-5"
      style={{
        background: 'rgba(3, 8, 14, 0.68)',
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
      }}
      data-testid="required-name-onboarding"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md rounded-[30px] p-5 sm:p-6"
        style={{
          background: 'linear-gradient(180deg, color-mix(in oklab, var(--vl-bg, #07131F) 92%, #FFFFFF), var(--vl-bg, #07131F))',
          border: `1px solid ${PLAYER_UI_ACCENT_BORDER}`,
          boxShadow: `0 22px 70px rgba(0,0,0,0.62), 0 0 44px ${PLAYER_UI_ACCENT_GLOW}`,
          color: '#FFFFFF',
        }}
      >
        <div className="flex flex-col items-center text-center">
          <div
            className="mb-4 flex h-20 w-20 items-center justify-center overflow-hidden rounded-full"
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '2px solid color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #004D61)) 58%, #FFFFFF 12%)',
              boxShadow: `0 0 24px ${PLAYER_UI_ACCENT_GLOW}`,
            }}
          >
            {brandLogo ? (
              <SafeImage src={brandLogo} alt={brandName} className="h-full w-full object-cover" fallback={<CircleUser className="h-9 w-9 text-white/80" />} />
            ) : (
              <CircleUser className="h-9 w-9 text-white/80" />
            )}
          </div>
          <p className="text-xs font-extrabold uppercase tracking-[0.32em]" style={{ color: 'var(--vl-neon, #004D61)' }}>
            Welcome to
          </p>
          <h2 className="display mt-1 text-2xl font-extrabold leading-tight">{brandName}</h2>
          <p className="mt-3 text-sm leading-relaxed text-white/70">
            Enter your full name to continue your journey.
          </p>
        </div>

        <div className="mt-5">
          <label className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/55" htmlFor="required-full-name">
            Full name
          </label>
          <Input
            id="required-full-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoFocus
            maxLength={40}
            placeholder="Enter your full name"
            className="mt-2 h-14 rounded-2xl text-base font-bold"
            style={{
              background: 'rgba(0,0,0,0.42)',
              borderColor: canSave ? 'var(--vl-neon, #004D61)' : 'rgba(255,255,255,0.16)',
              color: '#FFFFFF',
              boxShadow: canSave ? `0 0 18px ${PLAYER_UI_ACCENT_GLOW}` : 'none',
            }}
            data-testid="required-full-name-input"
          />
        </div>

        <Button
          type="submit"
          disabled={!canSave || saving}
          className="mt-5 h-13 w-full rounded-2xl display text-base font-extrabold"
          style={{
            background: canSave
              ? 'linear-gradient(135deg, var(--vl-btn-primary-from, var(--vl-neon, #004D61)), var(--vl-btn-primary-to, #004D61))'
              : 'rgba(255,255,255,0.12)',
            color: canSave ? 'var(--vl-btn-primary-text, #031A2A)' : 'rgba(255,255,255,0.48)',
            boxShadow: canSave ? `0 12px 28px ${PLAYER_UI_ACCENT_GLOW}` : 'none',
          }}
          data-testid="required-full-name-save"
        >
          {saving ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
          Continue
        </Button>
      </form>
    </div>
  );
}

export default function PlayerApp() {
  const { user, logout, checkAuth, tenantSlug: authTenantSlug } = useAuth();
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const tenantSlug = params.tenantSlug || authTenantSlug || null;

  const [loading, setLoading] = useState(true);
  const [playerDataReadyScope, setPlayerDataReadyScope] = useState('');
  const balanceSyncInFlightRef = useRef(false);
  const lastBalanceSyncAtRef = useRef(0);
  const walletSyncInFlightRef = useRef(false);
  const [data, setData] = useState(emptyPlayerData);
  const [wallet, setWallet] = useState({ balance: 0, pending: 0, play_credits: 0 });
  const [walletRules, setWalletRules] = useState({ withdrawal_rules: { min_amount: 20, max_amount: 5000, max_total_per_day: 0 } });
  const [badge, setBadge] = useState({ phone_verified: false, show_badge: false, tier: { key: 'none', color: '#3B82F6', label: 'Member' } });
  const [chatUnread, setChatUnread] = useState(0);
  const [chatAgentUnread, setChatAgentUnread] = useState([]);
  const [branding, setBranding] = useState({ logo_url: '', brand_name: '', brand_tagline: 'Player Lounge' });
  // Pull the active tenant's custom theme — its brand_name / logo_url /
  // tagline override the platform branding when set, so a tenant's
  // splash + header instantly reflect their identity.
  const { theme: tenantTheme, tenantSlug: themeTenantSlug } = useTenantTheme();
  const effectiveBranding = useMemo(() => {
    const userTenantName = cleanBrandLabel(user?.tenant_name || user?.tenant?.name);
    const themeBrandName = cleanBrandLabel(tenantTheme.brand_name);
    const apiBrandName = cleanBrandLabel(branding.brand_name);
    const brandName = (
      (themeTenantSlug && !isGenericPlayerBrandName(themeBrandName) && themeBrandName)
      || (!isGenericPlayerBrandName(apiBrandName) && apiBrandName)
      || userTenantName
      || themeBrandName
      || apiBrandName
      || ''
    );

    return {
      logo_url: (tenantTheme.logo_url && tenantTheme.logo_url.trim()) || branding.logo_url || '',
      brand_name: brandName,
      brand_tagline: (tenantTheme.tagline && tenantTheme.tagline.trim()) || branding.brand_tagline || 'Player Lounge',
      intro_logo_url: (tenantTheme.intro_logo_url && tenantTheme.intro_logo_url.trim()) || '',
      description: branding.description || '',
      verified: branding.verified,
      stats: branding.stats || {},
      socials: branding.socials || {},
      social_visibility: branding.social_visibility || {},
      support: branding.support || {},
      support_visibility: branding.support_visibility || {},
      support_icons: branding.support_icons || {},
      chat_agents: branding.chat_agents || [],
      home_screen: branding.home_screen || {},
    };
  }, [tenantTheme, themeTenantSlug, branding, user]);
  const showNotificationSetupNudge = shouldShowInstallSetupNudges({
    tenantSlug,
    branding: effectiveBranding,
    user,
  });
  const fullNameLooksAutoGenerated = Boolean(
    user &&
    user.profile_name_required !== false &&
    String(user.full_name || '').trim().length <= 1
  );
  const requiresNameOnboarding = Boolean(
    user &&
    user.role === 'user' &&
    (user.profile_name_required === true || fullNameLooksAutoGenerated)
  );

  const [walletOpen, setWalletOpen] = useState(false);
  const [walletMode, setWalletMode] = useState('deposit');
  const [walletStep, setWalletStep] = useState(1); // 1 = method, 2 = amount
  const [walletMethod, setWalletMethod] = useState(null);
  const [supportMenuOpen, setSupportMenuOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [selectedChatAgent, setSelectedChatAgent] = useState(null);
  const [addGameOpen, setAddGameOpen] = useState(false);
  const [gamesMenuOpen, setGamesMenuOpen] = useState(false);
  const [gamesHubTab, setGamesHubTab] = useState('my');
  // Lifted game-creation state — survives dialog close so a request keeps
  // running in the background, and the player can launch multiple games
  // in parallel. Shape: { [gameId]: { status: 'creating'|'success'|'error', game, error?, account? } }
  const [gameCreations, setGameCreations] = useState({});
  const [resetPwOpen, setResetPwOpen] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [editProfileMode, setEditProfileMode] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileSettingsOpen, setProfileSettingsOpen] = useState(false);
  const [balanceDetailOpen, setBalanceDetailOpen] = useState(false);
  const [announcementsOpen, setAnnouncementsOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationDetailOpen, setNotificationDetailOpen] = useState(false);
  const [notificationDetailItem, setNotificationDetailItem] = useState(null);
  const [achievementsOpen, setAchievementsOpen] = useState(false);
  const [notifUnread, setNotifUnread] = useState(0);
  const [homeAlertsOpen, setHomeAlertsOpen] = useState(false);
  const [homeAlertsLoading, setHomeAlertsLoading] = useState(false);
  const [homeAlertItems, setHomeAlertItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const playerSessionScope = user?.id ? `${tenantSlug || 'global'}:${user.id}` : '';
  const brandingScope = `branding:${tenantSlug || 'global'}`;
  const balanceCoachStorageKey = user?.id ? `vl_balance_coach_seen:${tenantSlug || 'global'}:${user.id}` : '';
  const pendingGamesStorageKey = scopedPlayerStorageKey(PENDING_GAMES_STORAGE_KEY, tenantSlug, user?.id);
  const favoritesStorageKey = scopedPlayerStorageKey(FAVORITES_STORAGE_KEY, tenantSlug, user?.id);
  const [favorites, setFavorites] = useState(() => new Set());
  const [balanceCoachVisible, setBalanceCoachVisible] = useState(false);
  const [focusGameAccountId, setFocusGameAccountId] = useState(null);
  const [pendingGameAccounts, setPendingGameAccounts] = useState([]);
  const previousChatUnreadRef = useRef(null);
  const chatUnreadRequestRef = useRef(0);
  const activePlayerScopeRef = useRef('');
  const activeBrandingScopeRef = useRef('');
  const brandingRefreshInFlightRef = useRef(false);
  const suppressOverlayHistoryRef = useRef(false);
  const overlayStackRef = useRef([]);
  const overlayRestoreRef = useRef({});
  const overlayReleaseTimerRef = useRef(null);
  const historyGuardPushTimerRef = useRef(null);
  const bottomNavSurfaceEventHandledRef = useRef(false);
  const bottomNavSurfaceEventTimerRef = useRef(null);
  const gameCreationTimersRef = useRef({});

  const clearGameCreationTimer = (gameId) => {
    const key = String(gameId || '');
    if (!key || !gameCreationTimersRef.current[key]) return;
    clearTimeout(gameCreationTimersRef.current[key]);
    delete gameCreationTimersRef.current[key];
  };

  const clearAllGameCreationTimers = () => {
    Object.values(gameCreationTimersRef.current || {}).forEach(timerId => clearTimeout(timerId));
    gameCreationTimersRef.current = {};
  };

  const releaseOverlayHistoryLockSoon = () => {
    if (overlayReleaseTimerRef.current) {
      clearTimeout(overlayReleaseTimerRef.current);
    }
    overlayReleaseTimerRef.current = setTimeout(() => {
      suppressOverlayHistoryRef.current = false;
    }, 0);
  };

  const withOverlayHistorySuppressed = (fn) => {
    suppressOverlayHistoryRef.current = true;
    fn?.();
    releaseOverlayHistoryLockSoon();
  };

  const playerHomePath = tenantSlug ? `/${tenantSlug}/app` : '/app';

  const fetchBranding = async ({ scope = brandingScope } = {}) => {
    const requestScope = scope || brandingScope;
    if (brandingRefreshInFlightRef.current) return null;
    brandingRefreshInFlightRef.current = true;
    try {
      const res = await freshGet('/api/branding', requestScope);
      if (activeBrandingScopeRef.current && activeBrandingScopeRef.current !== requestScope) return null;
      const nextBranding = res.data || {};
      setBranding(prev => ({ ...prev, ...nextBranding }));
      return nextBranding;
    } catch {
      return null;
    } finally {
      brandingRefreshInFlightRef.current = false;
    }
  };

  const pushPlayerHomeGuardBuffer = () => {
    if (typeof window === 'undefined') return;
    try {
      const nonce = Date.now();
      for (let i = 0; i < PLAYER_HISTORY_GUARD_DEPTH; i += 1) {
        window.history.pushState({
          playerHome: true,
          playerGuard: true,
          playerGuardSlot: i,
          playerNonce: `home-${nonce}-${i}`,
        }, '', playerHomePath);
      }
    } catch {
      // ignore history push failures
    }
  };

  const schedulePlayerHomeGuardBuffer = () => {
    if (typeof window === 'undefined') return;
    if (historyGuardPushTimerRef.current) {
      cancelIdleWork(historyGuardPushTimerRef.current);
    }
    historyGuardPushTimerRef.current = scheduleIdleWork(() => {
      pushPlayerHomeGuardBuffer();
      historyGuardPushTimerRef.current = null;
    }, { timeout: 220 });
  };

  const parkPlayerHistoryAtHome = ({ resetStack = false } = {}) => {
    if (typeof window === 'undefined') return;
    try {
      if (resetStack) overlayStackRef.current = [];
      const rootState = {
        playerRoot: true,
        playerGuard: true,
        playerNonce: `root-${Date.now()}`,
      };
      window.history.replaceState(rootState, '', playerHomePath);
      schedulePlayerHomeGuardBuffer();
    } catch {
      // ignore history push failures
    }
  };

  const normalizeCurrentHistoryToPlayerHome = () => {
    if (typeof window === 'undefined') return;
    try {
      window.history.replaceState({
        playerHome: true,
        playerNonce: `home-${Date.now()}`,
      }, '', playerHomePath);
    } catch {
      // ignore history replace failures
    }
  };

  const installPlayerHistoryTrap = () => {
    if (typeof window === 'undefined') return;
    try {
      parkPlayerHistoryAtHome({ resetStack: true });
    } catch {
      // ignore history push failures
    }
  };

  const pushOverlayHistory = (name) => {
    if (typeof window === 'undefined' || suppressOverlayHistoryRef.current) return;
    if (overlayStackRef.current[overlayStackRef.current.length - 1] === name) return;
    try {
      window.history.pushState({
        ...(window.history.state || {}),
        playerOverlay: name,
        overlayRestore: overlayRestoreRef.current[name] || null,
        overlayNonce: `${name}-${Date.now()}`,
      }, '');
      overlayStackRef.current.push(name);
      schedulePlayerHomeGuardBuffer();
    } catch {
      // ignore history push failures
    }
  };

  const dropOverlayFromStack = (name) => {
    const stack = overlayStackRef.current;
    const idx = stack.lastIndexOf(name);
    if (idx >= 0) stack.splice(idx, 1);
  };

  const replaceCurrentOverlayHistory = (name, restore = null) => {
    if (typeof window === 'undefined') return;
    try {
      window.history.replaceState({
        ...(window.history.state || {}),
        playerOverlay: name,
        overlayRestore: restore,
        overlayNonce: `${name}-${Date.now()}`,
      }, '');
      const stack = overlayStackRef.current;
      if (stack.length) stack[stack.length - 1] = name;
      else stack.push(name);
    } catch {
      // ignore history replace failures
    }
  };

  const closeOverlayState = (name) => {
    dropOverlayFromStack(name);
    if (name === 'reset-password') {
      setResetPwOpen(false);
      if (overlayRestoreRef.current['reset-password'] === 'profile') setProfileOpen(true);
      return;
    }
    if (name === 'wallet') {
      setWalletOpen(false);
      return;
    }
    if (name === 'balance-detail') {
      setBalanceDetailOpen(false);
      return;
    }
    if (name === 'profile-settings') {
      setProfileSettingsOpen(false);
      if (overlayRestoreRef.current['profile-settings'] === 'profile') setProfileOpen(true);
      return;
    }
    if (name === 'edit-profile') {
      setEditProfileOpen(false);
      setEditProfileMode(null);
      if (overlayRestoreRef.current['edit-profile'] === 'profile') setProfileOpen(true);
      return;
    }
    if (name === 'add-game') {
      setAddGameOpen(false);
      return;
    }
    if (name === 'profile') {
      setProfileOpen(false);
      return;
    }
    if (name === 'chat') {
      setChatOpen(false);
      setTimeout(() => fetchUnread(), 80);
      if (overlayRestoreRef.current.chat === 'support') setSupportMenuOpen(true);
      return;
    }
    if (name === 'alerts') {
      setNotificationsOpen(false);
      setNotificationDetailOpen(false);
      setNotificationDetailItem(null);
      if (overlayRestoreRef.current.alerts === 'support') setSupportMenuOpen(true);
      return;
    }
    if (name === 'notification-detail') {
      setNotificationDetailOpen(false);
      setNotificationDetailItem(null);
      return;
    }
    if (name === 'home-alerts') {
      setHomeAlertsOpen(false);
      return;
    }
    if (name === 'support') {
      setSupportMenuOpen(false);
      return;
    }
    if (name === 'announcements') {
      setAnnouncementsOpen(false);
      return;
    }
    if (name === 'achievements') {
      setAchievementsOpen(false);
    }
  };

  const closeOverlayWithBack = (name, fallbackClose) => {
    dropOverlayFromStack(name);
    fallbackClose?.();
    if (typeof window !== 'undefined' && window.history.state?.playerOverlay === name) {
      normalizeCurrentHistoryToPlayerHome();
    }
  };

  const dismissNavSurfaces = () => {
    const hasOpenSurface = (
      walletOpen ||
      balanceDetailOpen ||
      supportMenuOpen ||
      chatOpen ||
      notificationsOpen ||
      notificationDetailOpen ||
      homeAlertsOpen ||
      profileOpen ||
      profileSettingsOpen ||
      editProfileOpen ||
      resetPwOpen ||
      addGameOpen ||
      announcementsOpen ||
      achievementsOpen ||
      gamesMenuOpen ||
      Boolean(focusGameAccountId) ||
      overlayStackRef.current.length > 0
    );

    if (!hasOpenSurface) return;

    const navOverlayNames = [
      'wallet',
      'balance-detail',
      'support',
      'chat',
      'alerts',
      'notification-detail',
      'home-alerts',
      'profile',
      'profile-settings',
      'edit-profile',
      'reset-password',
      'add-game',
      'announcements',
      'achievements',
    ];

    navOverlayNames.forEach(dropOverlayFromStack);
    setWalletOpen(false);
    setBalanceDetailOpen(false);
    setSupportMenuOpen(false);
    setChatOpen(false);
    setSelectedChatAgent(null);
    setNotificationsOpen(false);
    setNotificationDetailOpen(false);
    setNotificationDetailItem(null);
    setHomeAlertsOpen(false);
    setProfileOpen(false);
    setProfileSettingsOpen(false);
    setEditProfileOpen(false);
    setEditProfileMode(null);
    setResetPwOpen(false);
    setAddGameOpen(false);
    setAnnouncementsOpen(false);
    setAchievementsOpen(false);
    setGamesMenuOpen(false);
    setFocusGameAccountId(null);

    if (typeof window !== 'undefined' && navOverlayNames.includes(window.history.state?.playerOverlay)) {
      normalizeCurrentHistoryToPlayerHome();
    }
  };

  const openSupportMenu = () => {
    dismissNavSurfaces();
    delete overlayRestoreRef.current.support;
    pushOverlayHistory('support');
    setSupportMenuOpen(true);
  };

  const closeSupportMenu = () => closeOverlayWithBack('support', () => setSupportMenuOpen(false));

  const keepBottomNavTapForNavigation = (event) => {
    const target = event?.target;
    const navItem = target?.closest?.('[data-player-bottom-nav]');
    const destination = navItem?.getAttribute?.('data-player-bottom-nav');
    if (!destination) return;

    event.preventDefault();
    event.stopPropagation?.();
    event.nativeEvent?.stopImmediatePropagation?.();
    bottomNavSurfaceEventHandledRef.current = true;
    if (bottomNavSurfaceEventTimerRef.current) {
      clearTimeout(bottomNavSurfaceEventTimerRef.current);
    }
    bottomNavSurfaceEventTimerRef.current = setTimeout(() => {
      bottomNavSurfaceEventHandledRef.current = false;
      bottomNavSurfaceEventTimerRef.current = null;
    }, 400);
    handleBottomNavIntent(destination);
  };

  const openChat = ({ restoreTo = null } = {}) => {
    if (restoreTo) overlayRestoreRef.current.chat = restoreTo;
    else delete overlayRestoreRef.current.chat;
    pushOverlayHistory('chat');
    setChatOpen(true);
  };

  const moveSupportToChat = (agent = null) => {
    withOverlayHistorySuppressed(() => {
      setSelectedChatAgent(agent || null);
      delete overlayRestoreRef.current.chat;
      replaceCurrentOverlayHistory('chat', null);
      setSupportMenuOpen(false);
      setChatOpen(true);
    });
  };

  const closeChat = () => closeOverlayWithBack('chat', () => {
    setChatOpen(false);
    fetchUnread();
  });

  const openAlertsPanel = ({ restoreTo = null } = {}) => {
    if (restoreTo) overlayRestoreRef.current.alerts = restoreTo;
    else delete overlayRestoreRef.current.alerts;
    pushOverlayHistory('alerts');
    setNotificationsOpen(true);
  };

  const closeAlertsPanel = () => {
    const activeOverlay = typeof window !== 'undefined' ? window.history.state?.playerOverlay : null;
    closeOverlayWithBack('alerts', () => {
      setNotificationsOpen(false);
      setNotificationDetailOpen(false);
      setNotificationDetailItem(null);
      dropOverlayFromStack('notification-detail');
    });
    if (activeOverlay === 'notification-detail') {
      normalizeCurrentHistoryToPlayerHome();
    }
  };

  const openNotificationDetail = (item) => {
    if (!item) return;
    setNotificationDetailItem(item);
    pushOverlayHistory('notification-detail');
    setNotificationDetailOpen(true);
  };

  const closeNotificationDetail = () => closeOverlayWithBack('notification-detail', () => {
    setNotificationDetailOpen(false);
    setNotificationDetailItem(null);
  });

  const openHomeAlertsPreview = () => {
    pushOverlayHistory('home-alerts');
    setHomeAlertsOpen(true);
    void loadHomeAlerts();
  };

  const closeHomeAlertsPreview = () => closeOverlayWithBack('home-alerts', () => setHomeAlertsOpen(false));

  const openAlertsFromHomePreview = () => {
    setHomeAlertsOpen(false);
    dropOverlayFromStack('home-alerts');
    if (typeof window !== 'undefined' && window.history.state?.playerOverlay === 'home-alerts') {
      delete overlayRestoreRef.current.alerts;
      replaceCurrentOverlayHistory('alerts', null);
      setNotificationsOpen(true);
      return;
    }
    openAlertsPanel();
  };

  const goToLobby = () => {
    tap();
    overlayStackRef.current = [];
    overlayRestoreRef.current = {};
    setWalletOpen(false);
    setWalletMode('deposit');
    setWalletMethod(null);
    setWalletStep(0);
    setGamesMenuOpen(false);
    setGamesHubTab('my');
    setSearchQuery('');
    setFocusGameAccountId(null);
    setSupportMenuOpen(false);
    setChatOpen(false);
    setSelectedChatAgent(null);
    setNotificationsOpen(false);
    setNotificationDetailOpen(false);
    setNotificationDetailItem(null);
    setHomeAlertsOpen(false);
    setProfileOpen(false);
    setProfileSettingsOpen(false);
    setEditProfileOpen(false);
    setEditProfileMode(null);
    setResetPwOpen(false);
    setAddGameOpen(false);
    setBalanceDetailOpen(false);
    setAnnouncementsOpen(false);
    setAchievementsOpen(false);
    try {
      navigate(playerHomePath, { replace: true });
    } catch {
      if (typeof window !== 'undefined') window.location.href = playerHomePath;
    }
    window.setTimeout(() => {
      try {
        window.scrollTo({ top: 0, behavior: 'auto' });
      } catch {
        // ignore scroll failures
      }
      normalizeCurrentHistoryToPlayerHome();
      schedulePlayerHomeGuardBuffer();
    }, 0);
  };

  const openProfile = ({ restoreTo = null } = {}) => {
    if (restoreTo) overlayRestoreRef.current.profile = restoreTo;
    else delete overlayRestoreRef.current.profile;
    pushOverlayHistory('profile');
    setProfileOpen(true);
  };

  const closeProfile = () => closeOverlayWithBack('profile', () => setProfileOpen(false));

  const openEditProfile = ({ restoreTo = null, mode = null } = {}) => {
    if (restoreTo) overlayRestoreRef.current['edit-profile'] = restoreTo;
    else delete overlayRestoreRef.current['edit-profile'];
    setEditProfileMode(mode);
    pushOverlayHistory('edit-profile');
    setEditProfileOpen(true);
  };

  const closeEditProfile = () => closeOverlayWithBack('edit-profile', () => {
    setEditProfileOpen(false);
    setEditProfileMode(null);
  });

  const openResetPassword = ({ restoreTo = null } = {}) => {
    if (restoreTo) overlayRestoreRef.current['reset-password'] = restoreTo;
    else delete overlayRestoreRef.current['reset-password'];
    pushOverlayHistory('reset-password');
    setResetPwOpen(true);
  };

  const closeResetPassword = () => closeOverlayWithBack('reset-password', () => setResetPwOpen(false));

  const openProfileSettings = ({ restoreTo = null } = {}) => {
    if (restoreTo) overlayRestoreRef.current['profile-settings'] = restoreTo;
    else delete overlayRestoreRef.current['profile-settings'];
    pushOverlayHistory('profile-settings');
    setProfileSettingsOpen(true);
  };

  const closeProfileSettings = () => closeOverlayWithBack('profile-settings', () => setProfileSettingsOpen(false));

  const openAddGame = () => {
    setGamesMenuOpen(false);
    delete overlayRestoreRef.current['add-game'];
    pushOverlayHistory('add-game');
    setAddGameOpen(true);
  };

  const closeAddGame = () => closeOverlayWithBack('add-game', () => setAddGameOpen(false));

  const openBalanceDetail = () => {
    dismissBalanceCoach();
    delete overlayRestoreRef.current['balance-detail'];
    pushOverlayHistory('balance-detail');
    setBalanceDetailOpen(true);
  };

  const closeBalanceDetail = () => closeOverlayWithBack('balance-detail', () => setBalanceDetailOpen(false));

  const openAnnouncements = () => {
    pushOverlayHistory('announcements');
    setAnnouncementsOpen(true);
  };

  const closeAnnouncements = () => closeOverlayWithBack('announcements', () => setAnnouncementsOpen(false));

  const openAchievements = () => {
    pushOverlayHistory('achievements');
    setAchievementsOpen(true);
  };

  const closeAchievements = () => closeOverlayWithBack('achievements', () => setAchievementsOpen(false));

  const dismissBalanceCoach = () => {
    setBalanceCoachVisible(false);
    if (!balanceCoachStorageKey) return;
    scheduleIdleWork(() => {
      try {
        localStorage.setItem(balanceCoachStorageKey, 'done');
      } catch {
        // Best-effort onboarding hint; storage failures should never block play.
      }
    }, { timeout: 1200 });
  };

  const toggleFavorite = (gameId) => {
    setFavorites(prev => {
      const next = new Set(prev);
      if (next.has(gameId)) next.delete(gameId); else next.add(gameId);
      const serialized = JSON.stringify([...next]);
      scheduleIdleWork(() => {
        try {
          localStorage.setItem(favoritesStorageKey, serialized);
        } catch {
          // Favorite state is still kept in memory if storage is unavailable.
        }
      }, { timeout: 1200 });
      return next;
    });
  };

  useEffect(() => {
    installPlayerHistoryTrap();
    // Fetch verified-badge payload (tier color + phone_verified gate)
    api.get('/api/me/badge').then(r => setBadge(b => ({ ...b, ...(r.data || {}) }))).catch(() => {});
    const t = setInterval(fetchUnread, 10000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    activeBrandingScopeRef.current = brandingScope;
    fetchBranding({ scope: brandingScope });

    const intervalId = setInterval(() => {
      fetchBranding({ scope: brandingScope });
    }, BRANDING_REFRESH_INTERVAL_MS);

    const refreshOnFocus = () => fetchBranding({ scope: brandingScope });
    const refreshOnVisible = () => {
      if (document.visibilityState === 'visible') refreshOnFocus();
    };

    window.addEventListener('focus', refreshOnFocus);
    document.addEventListener('visibilitychange', refreshOnVisible);
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', refreshOnFocus);
      document.removeEventListener('visibilitychange', refreshOnVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandingScope]);

  useEffect(() => {
    if (!selectedChatAgent) return;
    const agents = (Array.isArray(effectiveBranding.chat_agents) ? effectiveBranding.chat_agents : [])
      .filter(agent => agent && agent.enabled !== false && String(agent.name || '').trim());
    const selectedKey = chatAgentKey(selectedChatAgent);
    const latest = agents.find(agent => {
      if (!selectedKey) return false;
      if (chatAgentKey(agent) === selectedKey) return true;
      return String(agent.id || '') && String(agent.id) === String(selectedChatAgent.id || '');
    });

    if (!latest) {
      setSelectedChatAgent(null);
      return;
    }

    const normalizedLatest = {
      id: latest.id || selectedChatAgent.id || '',
      name: String(latest.name || '').trim(),
      title: latest.title || 'Support Agent',
      avatar_url: latest.avatar_url || '',
    };
    if (
      String(normalizedLatest.id || '') !== String(selectedChatAgent.id || '') ||
      normalizedLatest.name !== String(selectedChatAgent.name || '') ||
      normalizedLatest.title !== String(selectedChatAgent.title || '') ||
      normalizedLatest.avatar_url !== String(selectedChatAgent.avatar_url || '')
    ) {
      setSelectedChatAgent(normalizedLatest);
    }
  }, [effectiveBranding.chat_agents, selectedChatAgent]);

  useEffect(() => {
    if (loading || !user?.id || !balanceCoachStorageKey) return undefined;
    try {
      if (localStorage.getItem(balanceCoachStorageKey) === 'done') return undefined;
    } catch {
      // Continue with the hint if storage is unavailable.
    }

    const startTimer = setTimeout(() => setBalanceCoachVisible(true), 550);
    return () => {
      clearTimeout(startTimer);
    };
  }, [loading, user?.id, balanceCoachStorageKey]);

  useEffect(() => {
    // Auto-open profile when ?open=profile is present (e.g., from Explore "Verify phone" CTA)
    try {
      const params = new URLSearchParams(location.search || window.location.search);
      const openParam = params.get('open');
      if (openParam === 'profile') {
        openProfile();
      } else if (openParam === 'phone-verify') {
        // Land the user directly inside EditProfilePanel where phone
        // verification lives, but keep ProfileFullScreen behind it so
        // closing the editor returns the player to the profile screen.
        openProfile();
        openEditProfile({ restoreTo: 'profile', mode: 'phone-verify' });
      } else if (openParam === 'support' || openParam === 'chat') {
        openSupportMenu();
      } else if (openParam === 'wallet') {
        openWalletDashboard();
      } else if (openParam === 'games') {
        openGamesHub('my');
      }
      if (openParam) {
        // Clean the query string so refreshes don't re-trigger it.
        const cleaned = window.location.pathname + window.location.hash;
        window.history.replaceState({ playerHome: true, playerNonce: `home-${Date.now()}` }, '', cleaned);
      }
    } catch { /* ignore */ }
  }, [location.search]);

  useEffect(() => {
    if (!gamesMenuOpen || typeof document === 'undefined') return undefined;
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [gamesMenuOpen]);

  useEffect(() => {
    if (!user?.id || user?.role !== 'player') return undefined;

    let lastPresenceAt = 0;
    const sendPresence = () => {
      if (document.visibilityState === 'hidden') return;
      const now = Date.now();
      if (now - lastPresenceAt < 15000) return;
      lastPresenceAt = now;
      liveChatApi.post('/api/chat/presence/heartbeat').catch(() => {});
    };
    const markActivity = () => sendPresence();

    sendPresence();
    const interval = setInterval(sendPresence, 45000);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') sendPresence();
    };
    const activityEvents = ['pointerdown', 'keydown', 'touchstart', 'scroll', 'mousemove'];

    window.addEventListener('focus', sendPresence);
    activityEvents.forEach(eventName => window.addEventListener(eventName, markActivity, { passive: true }));
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', sendPresence);
      activityEvents.forEach(eventName => window.removeEventListener(eventName, markActivity));
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [user?.id, user?.role]);

  const accountMatchesPending = (pending, account) => {
    if (!pending || !account) return false;
    if (pending.id && account.id && String(pending.id) === String(account.id)) return true;
    if (pending.game_id && account.game_id && String(pending.game_id) === String(account.game_id)) return true;
    if (pending.game_code && account.game_code && String(pending.game_code) === String(account.game_code)) return true;
    return false;
  };

  const accountHasReadyCredentials = (account) => Boolean(
    account?.username ||
    account?.provider_username ||
    account?.password ||
    account?.provider_password ||
    account?.provider_account_id ||
    account?.provider_user_id
  );

  const accountReplacesPending = (pending, account) => (
    accountMatchesPending(pending, account) && accountHasReadyCredentials(account)
  );

  const toLocalOptimisticGameAccount = (account) => {
    if (!account?.id) return null;
    const now = new Date().toISOString();
    return {
      ...account,
      __creating: false,
      __localOptimistic: true,
      __localPinnedAt: account.__localPinnedAt || now,
      last_activity_at: account.last_activity_at || account.created_at || now,
    };
  };

  const dedupeLocalGameAccounts = (accounts) => {
    const next = [];
    accounts.forEach(account => {
      if (!account?.id) return;
      if (next.some(existing => (
        existing.id === account.id ||
        accountMatchesPending(existing, account)
      ))) return;
      next.push(account);
    });
    return next;
  };

  const mergeReadyServerAccounts = (serverAccounts) => {
    const readyAccounts = (serverAccounts || []).filter(accountHasReadyCredentials);

    setPendingGameAccounts(prev => {
      let changed = false;
      const next = prev.flatMap(pending => {
        const replacement = readyAccounts.find(serverAcc => accountReplacesPending(pending, serverAcc));
        if (!replacement) {
          if (pending.__localOptimistic) {
            changed = true;
            return [];
          }
          return [pending];
        }
        if (pending.__localOptimistic) {
          changed = true;
          return [];
        }

        const cachedReplacement = toLocalOptimisticGameAccount(replacement);
        changed = true;
        return cachedReplacement ? [cachedReplacement] : [];
      });
      const deduped = dedupeLocalGameAccounts(next);
      return changed || deduped.length !== prev.length ? deduped : prev;
    });
  };

  const removePendingGameAccounts = (matcher) => {
    setPendingGameAccounts(prev => {
      if (!prev.length) return prev;
      if (typeof matcher === 'string') {
        const next = prev.filter(acc => acc.id !== matcher);
        return next.length === prev.length ? prev : next;
      }
      const next = prev.filter(acc => !accountReplacesPending(acc, matcher));
      return next.length === prev.length ? prev : next;
    });
  };

  const pinLocalGameAccount = (account, options = {}) => {
    const localAccount = toLocalOptimisticGameAccount(account);
    if (!localAccount) return null;
    const replaceId = options.replaceId;
    setPendingGameAccounts(prev => dedupeLocalGameAccounts([
      localAccount,
      ...prev.filter(acc => acc.id !== replaceId),
    ]));
    setData(prev => {
      const previousAccounts = Array.isArray(prev.accounts) ? prev.accounts : [];
      return {
        ...prev,
        accounts: [
          localAccount,
          ...previousAccounts.filter(acc => (
            acc.id !== replaceId &&
            acc.id !== localAccount.id &&
            !accountMatchesPending(localAccount, acc)
          )),
        ],
      };
    });
    return localAccount;
  };

  useEffect(() => {
    if (!playerSessionScope) return;
    try {
      const serialized = JSON.stringify(pendingGameAccounts);
      scheduleIdleWork(() => {
        try {
          sessionStorage.setItem(pendingGamesStorageKey, serialized);
        } catch {
          // Ignore storage failures; the in-memory creating card still works.
        }
      }, { timeout: 1000 });
    } catch {}
  }, [pendingGameAccounts, pendingGamesStorageKey, playerSessionScope]);

  const upsertLinkedGameAccountFromNotification = (notif) => {
    const metadata = notif?.metadata || {};
    const linked = metadata.account || metadata.game_account || {};
    const accountId = linked.id || metadata.account_id;
    if (!accountId) return false;

    const nextBalance = Number(
      linked.last_known_balance ??
      linked.balance_usd ??
      metadata.verified_balance ??
      0
    ) || 0;
    const nextAccount = {
      id: accountId,
      user_id: linked.user_id || user?.id,
      tenant_id: linked.tenant_id || user?.tenant_id,
      game_id: linked.game_id || metadata.game_id || linked.game_code || metadata.game_code,
      game_code: linked.game_code || metadata.game_code || linked.game_id || metadata.game_id,
      game_name: linked.game_name || metadata.game_name || 'Game',
      username: linked.username || linked.provider_username || metadata.provider_username || '',
      password: linked.password || linked.provider_password || '',
      provider_code: linked.provider_code || metadata.provider_code || linked.game_code || metadata.game_code,
      provider_game_id: linked.provider_game_id || metadata.provider_game_id || linked.provider_code || metadata.provider_code,
      provider_account_id: linked.provider_account_id || metadata.provider_account_id || '',
      provider_user_id: linked.provider_user_id || metadata.provider_user_id || '',
      last_known_balance: nextBalance,
      balance_usd: nextBalance,
      last_activity_at: linked.last_activity_at || linked.created_at || new Date().toISOString(),
      last_balance_synced_at: linked.last_balance_synced_at || new Date().toISOString(),
      created_via: linked.created_via || 'admin_backend_link',
    };

    pinLocalGameAccount(nextAccount);
    return true;
  };

  // Real-time notification stream. Fires the moment the backend creates a
  // notification for this player (e.g. someone signs up with their referral
  // code, a deposit is approved, etc).
  useNotificationsWS({
    token: typeof window !== 'undefined' ? localStorage.getItem('session_token') : null,
    enabled: !!user?.id,
    onNotification: (notif) => {
      const title = String(notif?.title || 'New notification');
      const message = String(notif?.message || '');
      const category = String(notif?.category || '').toLowerCase();
      const actionUrl = String(notif?.action_url || '');
      const metadataEvent = String(notif?.metadata?.event || '').toLowerCase();
      const normalized = `${title} ${message}`.toLowerCase();
      const isLegacyPlayCreditLowBalance =
        normalized.includes('low balance warning') &&
        normalized.includes('play credits balance is low');

      if (isLegacyPlayCreditLowBalance) {
        return;
      }

      const isGameAffecting =
        category === 'game' ||
        actionUrl.startsWith('/games') ||
        metadataEvent === 'provider_account_linked' ||
        metadataEvent === 'game_account_created' ||
        normalized.includes('game account linked');

      const isWalletAffecting =
        category === 'deposit' ||
        category === 'wallet' ||
        actionUrl === '/wallet' ||
        isGameAffecting ||
        normalized.includes('approved and credited') ||
        normalized.includes('deposit approved') ||
        normalized.includes('wallet updated') ||
        normalized.includes('credited automatically') ||
        normalized.includes('redeemed from');

      if (
        metadataEvent === 'provider_account_linked' ||
        metadataEvent === 'game_account_created' ||
        normalized.includes('game account linked') ||
        normalized.includes('your game is ready')
      ) {
        upsertLinkedGameAccountFromNotification(notif);
      }

      if (isWalletAffecting) {
        void syncPlayerBalances({ force: true });
      }

      // Lift unread badge instantly — no need to wait for the 10s poll.
      setNotifUnread((n) => n + 1);
      setHomeAlertItems(prev => [notif, ...prev.filter(item => item.id !== notif?.id)].slice(0, 20));
      if (normalized.includes('low balance warning')) {
        return;
      }
      // Flash a toast so the user sees the event even if the bell panel isn't open.
      const isDepositApproved =
        category === 'deposit' &&
        (normalized.includes('approved') || normalized.includes('credited'));
      const isGameLoadOrRedeem =
        normalized.includes('game load success') ||
        normalized.includes('game redeem success') ||
        normalized.includes('loaded into') ||
        normalized.includes('redeemed from');
      const toastTitle = isDepositApproved ? 'Your credit has been added' : title;
      const toastDescription = isDepositApproved
        ? `${message} Your wallet is updated now.`
        : message;
      toast.success(toastTitle, {
        id: isGameLoadOrRedeem ? 'game-action-feedback' : 'player-live-alert',
        description: toastDescription,
        duration: 3000,
        action: isGameLoadOrRedeem ? undefined : {
          label: 'Open Alerts',
          onClick: () => {
            toast.dismiss();
            openAlerts();
          },
        },
      });
    },
    onCircleUpdate: (msg) => {
      // A downline (indirect) referrer's circle just got a new signup.
      // Show a transient toast — do NOT bump inbox count because the upline
      // doesn't get a persisted notification for indirect activity.
      if (msg?.event === 'referral_signup') {
        toast(`Network growth · depth ${msg.depth}`, {
          description: `${msg.new_username} joined your referral circle`,
          duration: 4000,
        });
      }
    },
  });

  const fetchUnread = async () => {
    const requestId = ++chatUnreadRequestRef.current;
    try {
      const res = await liveChatApi.get('/api/chat/unread-count');
      if (requestId !== chatUnreadRequestRef.current) return;
      const nextChatUnread = res.data.unread_count || 0;
      const nextAgentUnread = Array.isArray(res.data.by_agent) ? res.data.by_agent : [];
      const previousChatUnread = previousChatUnreadRef.current;
      setChatUnread(prev => (prev === nextChatUnread ? prev : nextChatUnread));
      setChatAgentUnread(prev => (sameUnreadAgents(prev, nextAgentUnread) ? prev : nextAgentUnread));
      if (
        previousChatUnread !== null &&
        nextChatUnread > previousChatUnread &&
        !chatOpen
      ) {
        const newestAgent = nextAgentUnread[0];
        toast('New live chat message', {
          id: 'live-chat-unread',
          description: newestAgent?.agent_name
            ? `${newestAgent.agent_name}: ${newestAgent.last_message || 'sent you a message'}`
            : `${nextChatUnread > 99 ? '99+' : nextChatUnread} unread message${nextChatUnread === 1 ? '' : 's'}`,
          duration: 2000,
          action: {
            label: 'Pick Agent',
            onClick: openLiveChatFromNotice,
          },
          onClick: openLiveChatFromNotice,
        });
      }
      previousChatUnreadRef.current = nextChatUnread;
    } catch {}
    try {
      const r2 = await api.get('/api/notifications/unread-count');
      const nextNotifUnread = r2.data.count || 0;
      setNotifUnread(prev => (prev === nextNotifUnread ? prev : nextNotifUnread));
    } catch {}
  };

  useEffect(() => {
    setBrowserAppBadge(notifUnread);
  }, [notifUnread]);

  const loadHomeAlerts = async () => {
    setHomeAlertsLoading(true);
    try {
      const res = await api.get('/api/notifications');
      setHomeAlertItems(res.data?.notifications || []);
    } catch {
      setHomeAlertItems([]);
    } finally {
      setHomeAlertsLoading(false);
    }
  };

  const handleHomeAlertsOpenChange = (open) => {
    if (open) {
      openHomeAlertsPreview();
    } else {
      closeHomeAlertsPreview();
    }
  };

  const markHomeAlertRead = async (item) => {
    if (!item) return;
    try {
      if (isNotificationUnread(item)) {
        await api.post(`/api/notifications/${item.id}/read`);
        setNotifUnread((count) => Math.max(0, count - 1));
        setHomeAlertItems(prev => prev.map(n => (
          n.id === item.id ? { ...n, read: true, is_read: true } : n
        )));
      }
    } catch {
      toast.error('Failed');
      return;
    }
    toast.dismiss();
    openAlertsFromHomePreview();
  };

  const markHomeAlertsReadAll = async () => {
    try {
      await api.post('/api/notifications/mark-all-read');
      setNotifUnread(0);
      setHomeAlertItems(prev => prev.map(n => ({ ...n, read: true, is_read: true })));
      toast.dismiss();
    } catch {
      toast.error('Failed');
    }
  };

  const openAllAlertsFromHome = () => {
    openAlertsFromHomePreview();
  };

  const resetPlayerRuntimeState = () => {
    clearAllGameCreationTimers();
    setData(emptyPlayerData());
    setPlayerDataReadyScope('');
    setWallet({ balance: 0, pending: 0, play_credits: 0 });
    setWalletRules({ withdrawal_rules: { min_amount: 20, max_amount: 5000, max_total_per_day: 0 } });
    setBadge({ phone_verified: false, show_badge: false, tier: { key: 'none', color: '#3B82F6', label: 'Member' } });
    setChatUnread(0);
    setChatAgentUnread([]);
    setNotifUnread(0);
    setHomeAlertItems([]);
    setSearchQuery('');
    setFocusGameAccountId(null);
    setGameCreations({});
    setPendingGameAccounts([]);
    setWalletOpen(false);
    setSupportMenuOpen(false);
    setChatOpen(false);
    setAddGameOpen(false);
    setProfileOpen(false);
    setProfileSettingsOpen(false);
    setBalanceDetailOpen(false);
    setNotificationsOpen(false);
    setHomeAlertsOpen(false);
    setAnnouncementsOpen(false);
    setAchievementsOpen(false);
    overlayStackRef.current = [];
    overlayRestoreRef.current = {};
    lastBalanceSyncAtRef.current = 0;
    walletSyncInFlightRef.current = false;
    balanceSyncInFlightRef.current = false;
  };

  const resolveUnreadChatAgent = () => {
    const unread = Array.isArray(chatAgentUnread)
      ? chatAgentUnread.find(item => Number(item?.unread_count || 0) > 0) || chatAgentUnread[0]
      : null;
    if (!unread) return null;

    const unreadId = String(unread.agent_id || '').trim();
    const unreadName = String(unread.agent_name || '').trim();
    const configuredAgents = (Array.isArray(effectiveBranding.chat_agents) ? effectiveBranding.chat_agents : [])
      .filter(agent => agent && agent.enabled !== false && String(agent.name || '').trim());

    const configured = configuredAgents.find(agent => {
      const agentId = String(agent.id || '').trim();
      const agentName = String(agent.name || '').trim().toLowerCase();
      return (unreadId && agentId === unreadId) || (unreadName && agentName === unreadName.toLowerCase());
    });

    return {
      id: configured?.id || unreadId || '',
      name: configured?.name || unreadName || 'Support Team',
      title: configured?.title || unread.agent_title || 'Support Agent',
      avatar_url: configured?.avatar_url || unread.avatar_url || '',
    };
  };

  const fetchData = async ({ scope = playerSessionScope, replaceAccounts = false, minimumLoadingMs = 0 } = {}) => {
    const requestScope = scope || playerSessionScope;
    if (!requestScope) {
      setLoading(false);
      return;
    }
    const startedAt = Date.now();
    setLoading(true);
    try {
      const [gamesRes, methodsRes, txnsRes, accsRes, walletRes, walletRulesRes] = await Promise.all([
        freshGet('/api/games/list', requestScope)
          .catch(() => freshGet('/api/public/games', requestScope))
          .catch(() => ({ data: { games: [] } })),
        api.get('/api/payment-methods', {
          params: { _ts: Date.now() },
          headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
        }).catch(() => ({ data: { items: [] } })),
        api.get('/api/wallet/transactions?limit=20')
          .catch(() => api.get('/api/requests/my-requests?limit=20'))
          .catch(() => ({ data: { transactions: [], requests: [] } })),
        freshGet('/api/games/accounts', requestScope).catch(() => ({ data: { accounts: [], __failed: true } })),
        api.get('/api/wallet/balance').catch(() => ({ data: { balance: 0 } })),
        api.get('/api/public/settings/wallet-rules').catch(() => ({ data: { withdrawal_rules: { min_amount: 20, max_amount: 5000, max_total_per_day: 0 } } })),
      ]);
      if (activePlayerScopeRef.current !== requestScope) return;
      setWallet(walletRes.data);
      setWalletRules(walletRulesRes.data || { withdrawal_rules: { min_amount: 20, max_amount: 5000, max_total_per_day: 0 } });
      const nextGames = Array.isArray(gamesRes.data)
        ? gamesRes.data
        : (gamesRes.data.games || gamesRes.data.items || []);
      const nextMethods = sortPlayerPaymentMethods(methodsRes.data.items || methodsRes.data.methods || []);
      const accountsFetchFailed = Boolean(accsRes.data.__failed);
      const serverAccounts = accountsFetchFailed
        ? []
        : (Array.isArray(accsRes.data.accounts) ? accsRes.data.accounts : []);
      if (!accountsFetchFailed) mergeReadyServerAccounts(serverAccounts);
      setData(prev => ({
        games: nextGames.length ? nextGames : prev.games,
        methods: nextMethods,
        transactions: txnsRes.data.requests || txnsRes.data.transactions || [],
        accounts: accountsFetchFailed
          ? prev.accounts
          : replaceAccounts
          ? serverAccounts
          : mergeServerAccountsPreservingLive(prev.accounts, serverAccounts),
      }));
    } finally {
      if (activePlayerScopeRef.current === requestScope) {
        const waitMs = Math.max(0, Number(minimumLoadingMs || 0) - (Date.now() - startedAt));
        if (waitMs > 0) {
          await new Promise(resolve => setTimeout(resolve, waitMs));
        }
        if (activePlayerScopeRef.current !== requestScope) return;
        setPlayerDataReadyScope(requestScope);
        setLoading(false);
        void syncPlayerBalances({ force: true, scope: requestScope });
      }
    }
  };

  const refreshPaymentMethods = async (flow = 'deposit') => {
    try {
      const methodsRes = await api.get('/api/payment-methods', {
        params: { flow, _ts: Date.now() },
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      });
      const nextMethods = sortPlayerPaymentMethods(methodsRes.data.items || methodsRes.data.methods || []);
      setData(prev => ({
        ...prev,
        methods: nextMethods,
      }));
    } catch {
      // Keep the wallet usable with the last known methods if the refresh fails.
    }
  };

  useEffect(() => {
    if (!user?.id) {
      activePlayerScopeRef.current = '';
      resetPlayerRuntimeState();
      setFavorites(new Set());
      setLoading(false);
      return;
    }

    activePlayerScopeRef.current = playerSessionScope;
    setLoading(true);
    setPlayerDataReadyScope('');
    try {
      sessionStorage.removeItem(PENDING_GAMES_STORAGE_KEY);
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('vl_player_accounts_cache')) sessionStorage.removeItem(key);
      });
    } catch {
      // ignore legacy storage cleanup failures
    }

    setPendingGameAccounts(readStoredArray(sessionStorage, pendingGamesStorageKey).filter(account => !account.__creating));
    setFavorites(new Set(readStoredArray(localStorage, favoritesStorageKey)));
    setData(emptyPlayerData());
    setWallet({ balance: 0, pending: 0, play_credits: 0 });
    setWalletRules({ withdrawal_rules: { min_amount: 20, max_amount: 5000, max_total_per_day: 0 } });
    setGameCreations({});
    setSearchQuery('');
    setFocusGameAccountId(null);
    setHomeAlertItems([]);
    lastBalanceSyncAtRef.current = 0;
    walletSyncInFlightRef.current = false;
    balanceSyncInFlightRef.current = false;

    fetchData({ scope: playerSessionScope, replaceAccounts: true, minimumLoadingMs: PLAYER_DATA_MIN_LOADING_MS });
    fetchUnread();
    api.get('/api/me/badge').then(r => {
      if (activePlayerScopeRef.current === playerSessionScope) {
        setBadge(b => ({ ...b, ...(r.data || {}) }));
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerSessionScope, user?.id, favoritesStorageKey, pendingGamesStorageKey]);

  const patchPlayerAccount = (accountId, patch = {}) => {
    if (!accountId) return;
    const nextPatch = {
      ...patch,
      updated_at: patch.updated_at || new Date().toISOString(),
    };
    setData(prev => ({
      ...prev,
      accounts: (Array.isArray(prev.accounts) ? prev.accounts : []).map(account => (
        account.id === accountId ? { ...account, ...nextPatch } : account
      )),
    }));
    setPendingGameAccounts(prev => prev.map(account => (
      account.id === accountId ? { ...account, ...nextPatch } : account
    )));
  };

  const syncPlayerBalances = async ({ force = false, liveBalances = false, scope = playerSessionScope } = {}) => {
    const requestScope = scope || playerSessionScope;
    if (!requestScope || activePlayerScopeRef.current !== requestScope) return;
    if (!liveBalances) {
      if (walletSyncInFlightRef.current) return;
      if (!force && Date.now() - lastBalanceSyncAtRef.current < 12000) return;
      walletSyncInFlightRef.current = true;
      try {
        const [walletRes, accsRes] = await Promise.all([
          api.get('/api/wallet/balance').catch(() => null),
          freshGet('/api/games/accounts', requestScope).catch(() => null),
        ]);
        if (activePlayerScopeRef.current !== requestScope) return;
        if (walletRes?.data) setWallet(walletRes.data);
        if (accsRes?.data) {
          const accountRows = Array.isArray(accsRes.data.accounts) ? accsRes.data.accounts : [];
          mergeReadyServerAccounts(accountRows);
          setData(prev => ({
            ...prev,
            accounts: mergeServerAccountsPreservingLive(prev.accounts, accountRows),
          }));
        }
      } finally {
        lastBalanceSyncAtRef.current = Date.now();
        walletSyncInFlightRef.current = false;
      }
      return;
    }

    const now = Date.now();
    if (balanceSyncInFlightRef.current) return;
    if (!force && now - lastBalanceSyncAtRef.current < 12000) return;
    balanceSyncInFlightRef.current = true;
    try {
      const [accsRes, walletRes] = await Promise.all([
        api.post('/api/games/accounts/refresh-balances?live=true')
          .catch(() => freshGet('/api/games/accounts', requestScope).catch(() => null)),
        api.get('/api/wallet/balance').catch(() => null),
      ]);
      if (activePlayerScopeRef.current !== requestScope) return;

      if (walletRes?.data) {
        setWallet(walletRes.data);
      }

      if (accsRes?.data) {
        const accountRows = Array.isArray(accsRes.data.accounts) ? accsRes.data.accounts : [];
        if (accsRes.data.ok === true) {
          setData(prev => ({
            ...prev,
            accounts: mergeLiveBalanceRows(prev.accounts, accountRows),
          }));
        } else {
          mergeReadyServerAccounts(accountRows);
          setData(prev => ({
            ...prev,
            accounts: mergeServerAccountsPreservingLive(prev.accounts, accountRows),
          }));
        }
      }
    } catch {
      // Keep background sync quiet. Card-level actions already surface errors.
    } finally {
      lastBalanceSyncAtRef.current = Date.now();
      balanceSyncInFlightRef.current = false;
    }
  };

  useEffect(() => {
    if (!user?.id) return undefined;

    const tick = () => {
      if (document.visibilityState === 'visible') {
        void syncPlayerBalances();
      }
    };

    const interval = setInterval(tick, 15000);
    document.addEventListener('visibilitychange', tick);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [user?.id]);

  useEffect(() => {
    const onPopState = (event) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      event?.stopImmediatePropagation?.();
      let handled = false;

      withOverlayHistorySuppressed(() => {
        if (typeof window !== 'undefined' && window.location.pathname !== playerHomePath) {
          try {
            window.history.replaceState({
              playerHome: true,
              playerGuard: true,
              playerNonce: `route-correct-${Date.now()}`,
            }, '', playerHomePath);
          } catch {
            // ignore route correction failures
          }
        }
        const stackedOverlay = overlayStackRef.current.pop();
        if (stackedOverlay) {
          closeOverlayState(stackedOverlay);
          schedulePlayerHomeGuardBuffer();
          handled = true;
          return;
        }
        if (notificationDetailOpen) {
          setNotificationDetailOpen(false);
          setNotificationDetailItem(null);
          schedulePlayerHomeGuardBuffer();
          handled = true;
          return;
        }
        if (resetPwOpen) {
          setResetPwOpen(false);
          const restore = overlayRestoreRef.current['reset-password'];
          if (restore === 'profile') setProfileOpen(true);
          schedulePlayerHomeGuardBuffer();
          handled = true;
          return;
        }
        if (balanceDetailOpen) {
          setBalanceDetailOpen(false);
          schedulePlayerHomeGuardBuffer();
          handled = true;
          return;
        }
        if (profileSettingsOpen) {
          setProfileSettingsOpen(false);
          const restore = overlayRestoreRef.current['profile-settings'];
          if (restore === 'profile') setProfileOpen(true);
          schedulePlayerHomeGuardBuffer();
          handled = true;
          return;
        }
        if (editProfileOpen) {
          setEditProfileOpen(false);
          setEditProfileMode(null);
          const restore = overlayRestoreRef.current['edit-profile'];
          if (restore === 'profile') setProfileOpen(true);
          schedulePlayerHomeGuardBuffer();
          handled = true;
          return;
        }
        if (addGameOpen) {
          setAddGameOpen(false);
          schedulePlayerHomeGuardBuffer();
          handled = true;
          return;
        }
        if (profileOpen) {
          setProfileOpen(false);
          schedulePlayerHomeGuardBuffer();
          handled = true;
          return;
        }
        if (chatOpen) {
          setChatOpen(false);
          setTimeout(() => fetchUnread(), 80);
          const restore = overlayRestoreRef.current.chat;
          if (restore === 'support') setSupportMenuOpen(true);
          schedulePlayerHomeGuardBuffer();
          handled = true;
          return;
        }
        if (notificationsOpen) {
          setNotificationsOpen(false);
          const restore = overlayRestoreRef.current.alerts;
          if (restore === 'support') setSupportMenuOpen(true);
          schedulePlayerHomeGuardBuffer();
          handled = true;
          return;
        }
        if (homeAlertsOpen) {
          setHomeAlertsOpen(false);
          schedulePlayerHomeGuardBuffer();
          handled = true;
          return;
        }
        if (supportMenuOpen) {
          setSupportMenuOpen(false);
          schedulePlayerHomeGuardBuffer();
          handled = true;
          return;
        }
        if (window.history.state?.playerRoot) {
          handled = true;
          schedulePlayerHomeGuardBuffer();
          return;
        }
        if (window.history.state?.playerHome) {
          handled = true;
          schedulePlayerHomeGuardBuffer();
          return;
        }
      });

      if (!handled) {
        schedulePlayerHomeGuardBuffer();
        suppressOverlayHistoryRef.current = false;
      }
    };

    window.addEventListener('popstate', onPopState, true);
    return () => {
      window.removeEventListener('popstate', onPopState, true);
      if (overlayReleaseTimerRef.current) {
        clearTimeout(overlayReleaseTimerRef.current);
      }
      if (historyGuardPushTimerRef.current) {
        cancelIdleWork(historyGuardPushTimerRef.current);
      }
      if (bottomNavSurfaceEventTimerRef.current) {
        clearTimeout(bottomNavSurfaceEventTimerRef.current);
      }
    };
  }, [playerHomePath, supportMenuOpen, chatOpen, notificationsOpen, notificationDetailOpen, homeAlertsOpen, profileOpen, profileSettingsOpen, editProfileOpen, resetPwOpen, addGameOpen, balanceDetailOpen, walletOpen, announcementsOpen, achievementsOpen]);

  const handleLogout = async () => {
    activePlayerScopeRef.current = '';
    resetPlayerRuntimeState();
    setFavorites(new Set());
    try {
      sessionStorage.removeItem(pendingGamesStorageKey);
      sessionStorage.removeItem(PENDING_GAMES_STORAGE_KEY);
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('vl_player_accounts_cache')) sessionStorage.removeItem(key);
      });
    } catch {
      // ignore logout storage cleanup failures
    }
    await logout();
    window.location.href = tenantSlug ? `/${tenantSlug}/auth` : '/auth';
  };

  const openPurchase = () => {
    dismissNavSurfaces();
    pushOverlayHistory('wallet');
    setWalletMode('deposit');
    setWalletMethod(null);
    setWalletStep(1);
    setWalletOpen(true);
    scheduleIdleWork(() => void refreshPaymentMethods('deposit'), { timeout: 450 });
  };
  const openWalletDashboard = () => {
    dismissNavSurfaces();
    pushOverlayHistory('wallet');
    setWalletMode('deposit');
    setWalletMethod(null);
    setWalletStep(0);
    setWalletOpen(true);
  };
  const openCashout  = () => {
    dismissNavSurfaces();
    pushOverlayHistory('wallet');
    setWalletMode('withdrawal');
    setWalletMethod(null);
    setWalletStep(1);
    setWalletOpen(true);
    scheduleIdleWork(() => void refreshPaymentMethods('withdraw'), { timeout: 450 });
  };
  const openGamesHub = (tab = 'my') => {
    dismissNavSurfaces();
    startTransition(() => {
      setGamesHubTab(tab);
      setGamesMenuOpen(true);
    });
  };
  const toggleGamesHubFromNav = () => {
    const shouldOpenGames = !gamesMenuOpen || walletOpen || balanceDetailOpen || supportMenuOpen || chatOpen;
    dismissNavSurfaces();
    if (shouldOpenGames) {
      startTransition(() => {
        setGamesHubTab('my');
        setGamesMenuOpen(true);
      });
    }
  };
  const goToRewards = () => {
    dismissNavSurfaces();
    navigate(tenantSlug ? `/${tenantSlug}/app/explore` : '/app/explore');
  };

  const handleBottomNavIntent = (destination) => {
    if (destination === 'rewards') {
      tap();
      goToRewards();
      return;
    }
    if (destination === 'games') {
      tap();
      toggleGamesHubFromNav();
      return;
    }
    if (destination === 'home') {
      goToLobby();
      return;
    }
    if (destination === 'wallets') {
      tap();
      openWalletDashboard();
      return;
    }
    if (destination === 'support') {
      tap();
      openSupportMenu();
    }
  };

  const openAlerts = () => {
    toast.dismiss();
    const restoreTo = supportMenuOpen ? 'support' : null;
    if (supportMenuOpen) {
      withOverlayHistorySuppressed(() => {
        setSupportMenuOpen(false);
        openAlertsPanel({ restoreTo });
      });
      return;
    }
    openAlertsPanel({ restoreTo });
  };

  const openLiveChatFromNotice = () => {
    toast.dismiss('live-chat-unread');
    const unreadAgent = resolveUnreadChatAgent();
    if (notificationsOpen) {
      setNotificationsOpen(false);
    }
    if (unreadAgent) {
      moveSupportToChat(unreadAgent);
    } else if (chatOpen) {
      withOverlayHistorySuppressed(() => {
        setChatOpen(false);
        setTimeout(() => setSupportMenuOpen(true), 180);
      });
    } else {
      setSupportMenuOpen(true);
    }
  };

  // Iter 145+: a global "open the deposit flow" trigger that any deeply
  // nested component (e.g. NoGameBalanceModal inside GameAccountCard, or a
  // promo banner) can fire via `window.dispatchEvent(new CustomEvent('vl:open-deposit'))`
  // without prop-drilling. Listener stays mounted for the lifetime of PlayerApp.
  useEffect(() => {
    const onOpenDeposit = () => openPurchase();
    const onOpenReferral = () => navigate(tenantSlug ? `/${tenantSlug}/app/referral` : '/app/referral');
    const onOpenSupport = () => openSupportMenu();
    window.addEventListener('vl:open-deposit', onOpenDeposit);
    window.addEventListener('vl:open-referral', onOpenReferral);
    window.addEventListener('vl:open-support', onOpenSupport);
    return () => {
      window.removeEventListener('vl:open-deposit', onOpenDeposit);
      window.removeEventListener('vl:open-referral', onOpenReferral);
      window.removeEventListener('vl:open-support', onOpenSupport);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantSlug]);

  // Kicks off a real /api/games/create-account request and tracks its
  // lifecycle in `gameCreations` so the UI can reflect creating /
  // success / error even after the Add Games dialog has been closed.
  // Multiple in-flight creations can run in parallel.
  const startGameCreation = (game) => {
    if (!game) return;
    const pendingAccountId = `creating-${game.id}-${Date.now()}`;
    const creationKey = game.id || pendingAccountId;
    const supportMessage = `We couldn't create your ${game.name || 'game'} ID. Please contact support.`;
    const pendingAccount = {
      id: pendingAccountId,
      user_id: user?.id,
      tenant_id: user?.tenant_id,
      game_id: game.id,
      game_code: game.code || game.id,
      game_name: game.name || 'Your game',
      username: '',
      password: '',
      provider_code: game.provider_code || game.provider || game.code || 'creating',
      provider_game_id: game.provider_game_id || game.provider_id || game.provider_code || game.code || game.id,
      image_url: game.logo_image_url || game.logo_url || game.image_url || game.image || null,
      last_known_balance: 0,
      balance_usd: 0,
      __creating: true,
      created_at: new Date().toISOString(),
    };
    setSearchQuery('');
    setPendingGameAccounts(prev => [
      pendingAccount,
      ...prev.filter(acc => !accountMatchesPending(pendingAccount, acc)),
    ]);
    setFocusGameAccountId(pendingAccountId);
    setTimeout(() => {
      setFocusGameAccountId(current => (current === pendingAccountId ? null : current));
    }, 1800);
    setGameCreations(prev => ({
      ...prev,
      [creationKey]: { status: 'creating', game, pendingAccountId, error: null },
    }));
    clearGameCreationTimer(creationKey);
    gameCreationTimersRef.current[String(creationKey)] = setTimeout(() => {
      removePendingGameAccounts(pendingAccountId);
      setFocusGameAccountId(current => (current === pendingAccountId ? null : current));
      setGameCreations(prev => {
        const current = prev[creationKey];
        if (!current || current.pendingAccountId !== pendingAccountId || current.status !== 'creating') return prev;
        return {
          ...prev,
          [creationKey]: { status: 'error', game, pendingAccountId: null, error: supportMessage },
        };
      });
      toast.error(supportMessage, {
        id: `game-create-${creationKey}`,
        duration: 3500,
      });
      setTimeout(() => {
        setGameCreations(prev => {
          if (prev[creationKey]?.error !== supportMessage) return prev;
          const next = { ...prev };
          delete next[creationKey];
          return next;
        });
      }, 4000);
      delete gameCreationTimersRef.current[String(creationKey)];
    }, GAME_CREATE_CARD_TIMEOUT_MS);
    api.post('/api/games/create-account', { game_id: game.id })
      .then(res => {
        clearGameCreationTimer(creationKey);
        const username = res.data?.username || res.data?.provider_username || '';
        const readyAccount = {
          ...res.data,
          last_activity_at: res.data?.last_activity_at || res.data?.created_at || new Date().toISOString(),
        };
        const localReadyAccount = pinLocalGameAccount(readyAccount, {
          replaceId: pendingAccountId,
        }) || readyAccount;
        setGameCreations(prev => ({
          ...prev,
          [creationKey]: { status: 'success', game, account: localReadyAccount, error: null },
        }));
        toast.success('Your game is ready', {
          id: 'player-live-alert',
          description: `${game.name} added${username ? ` — ${username}` : ''}`,
          duration: 3000,
        });
        void syncPlayerBalances({ force: true });
        setTimeout(() => {
          setGameCreations(prev => {
            const next = { ...prev };
            if (next[creationKey]?.status === 'success') delete next[creationKey];
            return next;
          });
        }, 2500);
      })
      .catch((err) => {
        clearGameCreationTimer(creationKey);
        const MAINTENANCE_MSG = 'Sorry, this game is under maintenance. Please try another game.';
        const status = err?.response?.status;
        const detail = String(err?.response?.data?.detail || err?.response?.data?.message || '').toLowerCase();
        const isDefinitiveFailure = status >= 400 && status < 500 && (
          detail.includes('maintenance') ||
          detail.includes('disabled') ||
          detail.includes('not available')
        );
        const message = isDefinitiveFailure ? MAINTENANCE_MSG : supportMessage;

        removePendingGameAccounts(pendingAccountId);
        setFocusGameAccountId(current => (current === pendingAccountId ? null : current));
        setGameCreations(prev => ({
          ...prev,
          [creationKey]: { status: 'error', game, error: message },
        }));
        toast.error(message, {
          id: `game-create-${creationKey}`,
          duration: 3500,
        });
        setTimeout(() => {
          setGameCreations(prev => {
            if (prev[creationKey]?.error !== message) return prev;
            const next = { ...prev };
            delete next[creationKey];
            return next;
          });
        }, 4000);
        void syncPlayerBalances();
      });
  };

  const balanceUsd = Number(wallet?.cash_balance ?? wallet?.balance ?? 0);
  const freePlayBalance = Math.floor(Number(wallet?.play_credits ?? wallet?.fp_credits ?? wallet?.freeplay ?? 0));
  const liveWinItems = useMemo(() => {
    const fallbackGames = ['Juwa', 'Juwa 2.0', 'Gameroom', 'Vegas Sweeps', 'Game Vault', 'Orion Stars'];
    const gameNames = (Array.isArray(data.games) ? data.games : [])
      .map(game => String(game?.name || game?.title || '').trim())
      .filter(Boolean);
    const names = ['Rona D', 'Jenny P', 'Aden M', 'Sofia K', 'Maya R', 'Nina B', 'Aria S', 'Luna V'];
    const amounts = [100, 250, 75, 500, 150, 320, 90, 680, 45, 210];
    const sourceGames = gameNames.length ? gameNames : fallbackGames;
    return sourceGames.slice(0, 12).map((gameName, index) => ({
      player: names[index % names.length],
      amount: amounts[index % amounts.length],
      gameName,
    }));
  }, [data.games]);

  const hotGames = useMemo(() => {
    const games = Array.isArray(data.games) ? data.games : [];
    const mappedGames = games.slice(0, 18).map((game, index) => {
      const rawImg = game.logo_image_url || game.logo_url || game.image_url || game.image || null;
      const hotImage = rawImg
        ? (String(rawImg).startsWith('http') ? rawImg : `${process.env.REACT_APP_BACKEND_URL || ''}${rawImg}`)
        : '/assets/player/balance-jackpot-bg-v2.png';
      return {
        id: game.id || game.code || `hot-game-${index}`,
        name: game.name || game.title || 'Game',
        image: hotImage,
      };
    });
    if (mappedGames.length) return mappedGames;
    return [
      { id: 'hot-juwa', name: 'Juwa', image: '/assets/player/balance-jackpot-bg-v2.png' },
      { id: 'hot-game-vault', name: 'Game Vault', image: '/assets/player/balance-jackpot-bg-v2.png' },
      { id: 'hot-fire-kirin', name: 'Fire Kirin', image: '/assets/player/balance-jackpot-bg-v2.png' },
      { id: 'hot-ultra-panda', name: 'Ultra Panda', image: '/assets/player/balance-jackpot-bg-v2.png' },
    ];
  }, [data.games]);

  const leaderboardItems = useMemo(() => {
    const botNames = [
      'RonaD', 'JennyAce', 'LuckyNova', 'SpinMaya', 'VaultKing', 'FireLux',
      'CashRider', 'OrionVip', 'PandaPop', 'VegasRay', 'Nina777', 'RoyalAden',
      'JuwaBoss', 'BlueMint', 'ZaraLoad', 'MaxWin', 'QueenFP', 'StarCash',
    ];
    const baseAmounts = [1180, 970, 845, 760, 690, 625, 580, 535];
    const games = hotGames.length ? hotGames : [{ name: 'Juwa' }, { name: 'Game Vault' }, { name: 'Fire Kirin' }];
    const daySeed = Math.floor(Date.now() / 86400000);
    return Array.from({ length: 6 }, (_, index) => {
      const username = botNames[(daySeed + index * 5) % botNames.length];
      const game = games[(daySeed + index * 3) % games.length]?.name || 'Juwa';
      const amount = baseAmounts[(daySeed + index * 2) % baseAmounts.length] + ((daySeed + index * 41) % 95);
      return {
        rank: index + 1,
        username,
        game,
        amount,
      };
    });
  }, [hotGames]);

  const myGameAccounts = useMemo(() => {
    const serverAccounts = Array.isArray(data.accounts) ? data.accounts : [];
    const visiblePending = pendingGameAccounts.filter(pending => (
      !serverAccounts.some(serverAcc => accountReplacesPending(pending, serverAcc))
    ));
    return [...visiblePending, ...serverAccounts];
  }, [data.accounts, pendingGameAccounts]);
  const surfaceOpen = (
    gamesMenuOpen ||
    walletOpen ||
    balanceDetailOpen ||
    supportMenuOpen ||
    chatOpen ||
    profileOpen ||
    profileSettingsOpen ||
    editProfileOpen ||
    resetPwOpen ||
    addGameOpen ||
    announcementsOpen ||
    notificationsOpen ||
    notificationDetailOpen ||
    achievementsOpen ||
    homeAlertsOpen
  );

  const loadingBrandLogo = effectiveBranding.intro_logo_url
    || effectiveBranding.logo_url
    || '/api/tenants/public/slotsoceans/install-icon';
  const activeBottomNav = supportMenuOpen || chatOpen
    ? 'support'
    : gamesMenuOpen
      ? 'games'
      : walletOpen || balanceDetailOpen
        ? 'wallets'
        : 'home';
  const bottomNavActiveIndex = {
    rewards: 0,
    games: 1,
    home: 2,
    wallets: 3,
    support: 4,
  }[activeBottomNav] ?? 2;
  const bottomNavButtonClass = (key, extra = '') => [
    'player-bottom-card-nav-item',
    activeBottomNav === key ? 'player-bottom-card-nav-item--active' : '',
    extra,
  ].filter(Boolean).join(' ');
  const handleBottomNavSurfaceEvent = (event) => {
    if (event?.type === 'click' && bottomNavSurfaceEventHandledRef.current) {
      event.preventDefault();
      event.stopPropagation();
      event.nativeEvent?.stopImmediatePropagation?.();
      bottomNavSurfaceEventHandledRef.current = false;
      if (bottomNavSurfaceEventTimerRef.current) {
        clearTimeout(bottomNavSurfaceEventTimerRef.current);
        bottomNavSurfaceEventTimerRef.current = null;
      }
      return;
    }
    if (!surfaceOpen) return;
    const target = event?.target;
    const navItem = target?.closest?.('[data-player-bottom-nav]');
    const destination = navItem?.getAttribute?.('data-player-bottom-nav');
    if (!destination) return;

    event.preventDefault();
    event.stopPropagation();
    event.nativeEvent?.stopImmediatePropagation?.();
    if (event?.type === 'pointerdown') {
      bottomNavSurfaceEventHandledRef.current = true;
      if (bottomNavSurfaceEventTimerRef.current) {
        clearTimeout(bottomNavSurfaceEventTimerRef.current);
      }
      bottomNavSurfaceEventTimerRef.current = setTimeout(() => {
        bottomNavSurfaceEventHandledRef.current = false;
        bottomNavSurfaceEventTimerRef.current = null;
      }, 400);
    }
    handleBottomNavIntent(destination);
  };

  const waitingForPlayerData = !user?.id || loading || playerDataReadyScope !== playerSessionScope;
  if (waitingForPlayerData) {
    return (
      <div className="player-app player-lobby-refresh min-h-[100dvh] flex items-center justify-center px-6">
        <div className="player-lobby-refresh-content flex flex-col items-center text-center" data-testid="player-lobby-loading" role="status" aria-live="polite">
          <div className="relative mb-5 grid h-32 w-32 place-items-center">
            <span className="absolute inset-0 rounded-full player-lobby-logo-ring" aria-hidden />
            <span className="absolute inset-3 rounded-full bg-[#001735]/75 blur-md" aria-hidden />
            <div
              className="player-lobby-refresh-logo relative grid h-28 w-28 place-items-center overflow-hidden rounded-full border"
              style={{
                borderColor: 'rgba(245,215,110,0.88)',
                background: 'rgba(0,23,53,0.76)',
              }}
            >
              <SafeImage
                src={loadingBrandLogo}
                alt="Brand logo"
                className="h-full w-full object-contain"
                loading="eager"
                fallback={<Crown className="h-12 w-12 text-[#F5D76E]" />}
              />
            </div>
          </div>
          <p className="player-lobby-refresh-brand display text-sm font-black uppercase">{effectiveBranding.brand_name || 'Slots Oceans'}</p>
          <p className="display mt-2 text-2xl font-extrabold text-white">Loading your lobby</p>
          <p className="mt-2 max-w-xs text-sm text-white/75">Syncing your latest games and wallet.</p>
          <span className="player-lobby-refresh-progress mt-6" aria-hidden><span /></span>
        </div>
      </div>
    );
  }

  return (
    <div className={`player-app player-desktop-side-nav-host min-h-[100dvh] flex flex-col ${surfaceOpen ? 'player-app--surface-open' : ''}`} data-testid="player-app">
      <style>{`
        @keyframes live-win-slide {
          0% { opacity: 0; transform: translateY(8px); }
          14%, 82% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-8px); }
        }
        @keyframes hot-games-marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .hot-games-track {
          animation: hot-games-marquee 34s linear infinite;
          will-change: transform;
        }
        .hot-games-track:hover { animation-play-state: paused; }
        .player-app--surface-open .hot-games-track,
        .player-app--surface-open .tap-pop-logo,
        .player-app--surface-open .tap-pop-balance,
        .player-app--surface-open .tap-pop-alert,
        .player-app--surface-open .tap-pop-profile {
          animation-play-state: paused;
        }
      `}</style>
      {/* Header — fixed so it stays visible when scrolling.
          Background pulls from the active tenant's `--vl-bg` so the
          header always blends with the page (preventing a black band on
          top of a coloured tenant background). */}
      <div className="player-home-topbar fixed top-0 left-0 right-0 z-50">
        <header className="player-home-header px-3 sm:px-4 py-3" style={{
          background: 'var(--vl-bg, #000000)',
        }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-2 sm:gap-3">
          <button
            type="button"
            onClick={() => navigate(tenantSlug ? `/${tenantSlug}/app/brand` : '/app/brand')}
            className="flex items-center gap-2 sm:gap-3 transition active:scale-[0.98] hover:brightness-110 rounded-xl px-0.5 sm:px-1 min-w-0 text-left"
            aria-label="Connect with us"
            title="Connect with us"
            data-testid="header-brand-button"
          >
            <span className="tap-pop-logo flex h-14 w-14 sm:h-16 sm:w-16 shrink-0 items-center justify-center rounded-full">
              <span
                className="flex h-full w-full items-center justify-center overflow-hidden rounded-full border"
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  borderColor: 'rgba(255,255,255,0.14)',
                  boxShadow: '0 10px 26px rgba(0,0,0,0.36)',
                }}
              >
                {effectiveBranding.logo_url ? (
                  <SafeImage
                    src={effectiveBranding.logo_url}
                    alt={effectiveBranding.brand_name}
                    className="w-full h-full object-cover"
                    data-testid="header-brand-logo"
                    loading="eager"
                    fetchPriority="high"
                    fallback={<Crown className="w-8 h-8" style={{ color: 'rgba(255,255,255,0.78)' }} />}
                  />
                ) : (
                  <Crown className="w-8 h-8" style={{ color: 'rgba(255,255,255,0.78)' }} />
                )}
              </span>
            </span>
            <div className="hidden sm:block text-left">
              <span
                className="display text-base font-extrabold tracking-wide uppercase"
                style={{ color: '#fff' }}
                data-testid="header-brand-name"
              >
                {effectiveBranding.brand_name || 'Our Lounge'}
              </span>
              <p className="text-[10px] uppercase tracking-[0.2em]" style={{ color: '#fff' }}>
                {effectiveBranding.brand_tagline || 'Player Lounge'}
              </p>
            </div>
          </button>

          <div className="flex items-center gap-1.5 sm:gap-3 min-w-0">
            <button
              type="button"
              onClick={() => { tap(); openWalletDashboard(); }}
              className="tap-pop-balance player-wallet-pill rounded-2xl pl-1.5 sm:pl-2 pr-2.5 sm:pr-4 py-1.5 flex items-center gap-1.5 sm:gap-2.5 min-w-0 transition hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-300/80"
              style={{
                background: 'linear-gradient(135deg, rgba(168,85,247,0.18), rgba(109,40,217,0.10))',
                border: '1px solid rgba(168,85,247,0.5)',
              }}
              data-testid="header-balance-display"
              aria-label={`Open wallet. Wallet balance $${balanceUsd.toFixed(2)} and ${freePlayBalance.toLocaleString()} FP`}
              title="Open wallet"
            >
              <span
                className="flex items-center gap-1.5 sm:gap-2.5 min-w-0"
                title="Wallet balance"
                aria-hidden="true"
              >
                <span
                  className="player-wallet-icon w-8 h-8 sm:w-9 sm:h-9 rounded-xl flex items-center justify-center shrink-0"
                  style={{
                    background: 'radial-gradient(circle at 30% 30%, #F0ABFC 0%, #A855F7 45%, #6D28D9 95%)',
                    boxShadow: 'inset 0 0 10px rgba(255,255,255,0.4), 0 0 14px rgba(168,85,247,0.6)',
                  }}
                >
                  <Wallet className="w-5 h-5" style={{ color: '#fff' }} />
                </span>
                <span className="text-left leading-tight min-w-0">
                  <span className="block text-[9px] sm:text-[10px] uppercase tracking-wider" style={{ color: '#fff' }}>
                    Balance
                  </span>
                  <span
                    className="display block text-base sm:text-lg font-extrabold"
                    style={{
                      color: 'var(--vl-neon, #22FF88)',
                      textShadow: '0 0 12px color-mix(in oklab, var(--vl-neon, #22FF88) 55%, transparent)',
                    }}
                    data-testid="header-balance-usd"
                  >
                    ${balanceUsd.toFixed(2)}
                  </span>
                </span>
              </span>
              <span
                className="display text-[11px] sm:text-xs font-extrabold whitespace-nowrap ml-0.5 sm:ml-1 select-none"
                style={{ color: '#FACC15', textShadow: '0 0 10px rgba(250,204,21,0.45)' }}
                data-testid="header-balance-fp"
                title="Freeplay credits"
              >
                {freePlayBalance.toLocaleString()} FP
              </span>
            </button>

            <HeaderAlertsButton
              unread={notifUnread}
              open={homeAlertsOpen}
              onOpenChange={handleHomeAlertsOpenChange}
              loading={homeAlertsLoading}
              items={homeAlertItems}
              onItemClick={markHomeAlertRead}
              onMarkAll={markHomeAlertsReadAll}
              onViewAll={openAllAlertsFromHome}
            />

            <ProfileTrigger
              user={user}
              badge={badge}
              onOpen={() => { tap(); openProfile(); }}
            />
          </div>
        </div>
      </header>
      </div>
      <div className="player-home-header-spacer" aria-hidden="true" />

      <div className="mx-auto w-full max-w-6xl px-3 pt-2 sm:px-4">
        <LiveWinsStrip items={liveWinItems} paused={surfaceOpen} />
        <div data-testid="home-promo-showcase-card">
          <PromoBanners variant="hero" />
        </div>

        {showNotificationSetupNudge && (
          <div className="mt-3 space-y-2" data-testid="home-install-notification-nudges">
            <AllowNotificationsNudge tenantSlug={tenantSlug} userId={user?.id} />
          </div>
        )}

        {hotGames.length > 0 && (
          <section className="mt-4 overflow-hidden pb-2" data-testid="home-hot-games">
            <div className="mb-2 flex items-center justify-between px-1">
              <div className="flex items-center gap-2">
                <Flame className="h-5 w-5 text-[#FFE66D]" strokeWidth={2.6} />
                <h2
                  className="text-lg font-black text-white"
                  style={{ fontFamily: "'Cinzel', 'Marcellus', Georgia, serif", textShadow: '0 2px 10px rgba(0,0,0,0.55)' }}
                >
                  Hot Games
                </h2>
              </div>
              <button
                type="button"
                onClick={() => { tap(); openGamesHub('available'); }}
                className="flex items-center gap-1 rounded-full px-2 py-1 text-[12px] font-extrabold text-[#FFE66D] transition active:scale-95"
                data-testid="home-hot-games-view-all"
              >
                View All <ChevronRight className="h-4 w-4" strokeWidth={2.8} />
              </button>
            </div>
            <div className="relative -mx-3 overflow-hidden sm:-mx-4">
              <div className="hot-games-track flex w-max gap-3 px-3 pb-1 sm:px-4">
                {[...hotGames, ...hotGames].map((game, index) => (
                  <button
                    key={`${game.id}-${index}`}
                    type="button"
                    onClick={() => { tap(); openGamesHub('available'); }}
                    className="w-[106px] shrink-0 text-left transition active:scale-95 sm:w-[122px]"
                    data-testid={`home-hot-game-${game.id}`}
                  >
                    <span
                      className="block aspect-square overflow-hidden rounded-[14px] border bg-black/50 shadow-[0_12px_22px_rgba(0,0,0,0.28)]"
                      style={{ borderColor: 'rgba(255,255,255,0.15)' }}
                    >
                      <SafeImage
                        src={game.image}
                        alt={game.name}
                        className="h-full w-full object-cover"
                        fallback={<div className="grid h-full w-full place-items-center bg-black/40"><Gamepad2 className="h-8 w-8 text-white/55" /></div>}
                      />
                    </span>
                    <span className="mt-1 block truncate text-center text-[12px] font-bold text-white/88">{game.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </section>
        )}

        {leaderboardItems.length > 0 && (
          <section
            className="mx-2 mt-3 rounded-[22px] border p-3 sm:mx-4"
            style={{
              background: '#004D61',
              borderColor: 'rgba(224,244,247,0.22)',
              boxShadow: '0 14px 28px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.07)',
            }}
            data-testid="home-leaderboard"
          >
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-[#FFE66D]" strokeWidth={2.6} />
                <h2
                  className="text-lg font-black text-white"
                  style={{ fontFamily: "'Cinzel', 'Marcellus', Georgia, serif", textShadow: '0 2px 10px rgba(0,0,0,0.55)' }}
                >
                  Leaderboard
                </h2>
              </div>
            </div>
            <div className="space-y-2">
              {leaderboardItems.map(item => (
                <div
                  key={`${item.rank}-${item.username}`}
                  className="flex items-center gap-3 rounded-[16px] border px-3 py-2"
                  style={{ background: 'rgba(3,18,24,0.3)', borderColor: 'rgba(224,244,247,0.14)' }}
                >
                  <span
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-[12px] font-black"
                    style={{
                      background: item.rank === 1 ? 'linear-gradient(135deg, #FFE66D, #F5BC4B)' : 'rgba(255,255,255,0.08)',
                      color: item.rank === 1 ? '#1A1100' : '#FFFFFF',
                    }}
                  >
                    #{item.rank}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-black text-white">@{item.username}</span>
                    <span className="block truncate text-[11px] font-semibold text-white/58">Won on {item.game}</span>
                  </span>
                  <span className="shrink-0 text-sm font-black text-[#FFE66D]">${item.amount}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      <main className="flex-1 pb-32" data-testid="player-home-empty-main" />

      {gamesMenuOpen && (
        <section
          className="fixed inset-x-0 top-0 z-[55] overflow-hidden overscroll-none"
          style={{
            bottom: '0px',
            background: 'linear-gradient(180deg, rgba(10,22,34,0.98) 0%, rgba(4,13,20,0.98) 100%)',
          }}
          data-testid="games-fullscreen-hub"
        >
          <div className="player-games-hub mx-auto flex h-full w-full max-w-6xl flex-col px-3 pt-2 sm:px-4">
            <div
              className="player-games-hub-tabs mb-3 grid grid-cols-2 rounded-[16px] border p-1"
              style={{ borderColor: 'rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.055)' }}
              role="tablist"
              aria-label="Games sections"
              data-testid="games-hub-tabs"
            >
              <button
                type="button"
                onClick={() => { tap(); setGamesHubTab('my'); }}
                className="rounded-[14px] px-2.5 py-2 text-xs font-extrabold transition active:scale-[0.98] sm:text-sm"
                style={{
                  background: gamesHubTab === 'my' ? 'linear-gradient(135deg, #006B85, #004D61)' : 'transparent',
                  color: gamesHubTab === 'my' ? '#FFFFFF' : 'rgba(255,255,255,0.74)',
                  boxShadow: gamesHubTab === 'my' ? '0 6px 18px rgba(0,107,133,0.24)' : 'none',
                }}
                role="tab"
                aria-selected={gamesHubTab === 'my'}
                data-testid="games-hub-tab-my"
              >
                My Games
              </button>
              <button
                type="button"
                onClick={() => { tap(); setGamesHubTab('available'); }}
                className="rounded-[14px] px-2.5 py-2 text-xs font-extrabold transition active:scale-[0.98] sm:text-sm"
                style={{
                  background: gamesHubTab === 'available' ? 'linear-gradient(135deg, #004D61, #075F75)' : 'transparent',
                  color: gamesHubTab === 'available' ? '#031A2A' : 'rgba(255,255,255,0.74)',
                  boxShadow: gamesHubTab === 'available' ? '0 6px 18px rgba(0,77,97,0.18)' : 'none',
                }}
                role="tab"
                aria-selected={gamesHubTab === 'available'}
                data-testid="games-hub-tab-available"
              >
                Available Games
              </button>
            </div>

            <div
              className="player-games-hub-card min-h-0 flex-1 overflow-hidden rounded-t-[26px] rounded-b-none border border-b-0 p-3 sm:p-4"
              style={{ borderColor: 'rgba(255,255,255,0.10)', background: 'rgba(0,0,0,0.18)' }}
            >
              {gamesHubTab === 'available' ? (
                <div className="flex h-full min-h-0 flex-col" data-testid="games-hub-available-panel">
                  <AddGamePanel
                    games={data.games}
                    accounts={myGameAccounts}
                    refresh={fetchData}
                    close={() => setGamesMenuOpen(false)}
                    gameCreations={gameCreations}
                    onStartCreate={startGameCreation}
                  />
                </div>
              ) : (
                <div className="player-games-hub-scroll h-full min-h-0 overflow-y-auto pr-0" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }} data-testid="games-hub-my-games-panel">
                  <MyGamesSection
                    accounts={myGameAccounts}
                    games={data.games}
                    wallet={wallet}
                    favorites={favorites}
                    onToggleFav={toggleFavorite}
                    onAdd={() => setGamesHubTab('available')}
                    refresh={fetchData}
                    onBackgroundSync={syncPlayerBalances}
                    onAccountPatch={patchPlayerAccount}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    focusAccountId={focusGameAccountId}
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {!chatOpen && (
      <nav
        className="player-bottom-card-nav-shell fixed bottom-0 left-0 right-0 z-[60] w-full px-0 pb-0"
        aria-label="Player quick navigation"
        data-testid="player-bottom-card-nav"
        onPointerDownCapture={handleBottomNavSurfaceEvent}
        onClickCapture={handleBottomNavSurfaceEvent}
      >
        <div
          className="player-bottom-card-nav relative grid grid-cols-5 items-center rounded-t-[22px] rounded-b-none border px-2 py-2 shadow-2xl backdrop-blur-xl"
          style={{
            '--nav-active-index': bottomNavActiveIndex,
            background: 'linear-gradient(135deg, rgba(9,17,27,0.94), rgba(16,24,39,0.9))',
            border: '1.5px solid rgba(255,255,255,0.24)',
            boxShadow: '0 18px 42px rgba(0,0,0,0.56), 0 0 28px rgba(0,77,97,0.14), inset 0 1px 0 rgba(255,255,255,0.08)',
          }}
        >
          <button
            type="button"
            onClick={() => handleBottomNavIntent('rewards')}
            className={bottomNavButtonClass('rewards')}
            data-player-bottom-nav="rewards"
            data-testid="bottom-nav-rewards"
            aria-current={activeBottomNav === 'rewards' ? 'page' : undefined}
          >
            <span className="player-bottom-card-nav-icon"><Gift className="h-5 w-5" /></span>
            <span className="player-bottom-card-nav-label">Rewards</span>
          </button>

          <button
            type="button"
            onClick={() => handleBottomNavIntent('games')}
            className={bottomNavButtonClass('games')}
            data-player-bottom-nav="games"
            data-testid="bottom-nav-games"
            aria-expanded={gamesMenuOpen}
            aria-current={activeBottomNav === 'games' ? 'page' : undefined}
          >
            <span className="player-bottom-card-nav-icon"><Gamepad2 className="h-5 w-5" /></span>
            <span className="player-bottom-card-nav-label">Games</span>
          </button>

          <button
            type="button"
            onClick={() => handleBottomNavIntent('home')}
            className={bottomNavButtonClass('home')}
            aria-label="Home"
            data-player-bottom-nav="home"
            data-testid="bottom-nav-logo"
            aria-current={activeBottomNav === 'home' ? 'page' : undefined}
          >
            <span className="player-bottom-card-nav-icon"><Home className="h-5 w-5" /></span>
            <span className="player-bottom-card-nav-label">Home</span>
          </button>

          <button
            type="button"
            onClick={() => handleBottomNavIntent('wallets')}
            className={bottomNavButtonClass('wallets')}
            data-player-bottom-nav="wallets"
            data-testid="bottom-nav-wallets"
            aria-current={activeBottomNav === 'wallets' ? 'page' : undefined}
          >
            <span className="player-bottom-card-nav-icon"><Wallet className="h-5 w-5" /></span>
            <span className="player-bottom-card-nav-label">Wallets</span>
          </button>

          <button
            type="button"
            onClick={() => handleBottomNavIntent('support')}
            className={bottomNavButtonClass('support', 'relative')}
            data-player-bottom-nav="support"
            data-testid="bottom-nav-support"
            aria-current={activeBottomNav === 'support' ? 'page' : undefined}
          >
            <span className="player-bottom-card-nav-icon"><Headphones className="h-5 w-5" /></span>
            <span className="player-bottom-card-nav-label">Support</span>
            {chatUnread > 0 && (
              <span className="absolute right-2 top-1 grid min-h-[18px] min-w-[18px] place-items-center rounded-full bg-red-500 px-1 text-[9px] font-black leading-none text-white">
                {chatUnread > 99 ? '99+' : chatUnread}
              </span>
            )}
          </button>
        </div>
      </nav>
      )}

      {requiresNameOnboarding && (
        <RequiredNameOnboarding
          user={user}
          branding={effectiveBranding}
          onComplete={checkAuth}
        />
      )}

      {/* Support menu sheet */}
      <Sheet open={supportMenuOpen} onOpenChange={(o) => { if (o) openSupportMenu(); else closeSupportMenu(); }}>
        <SheetContent
          side="right"
          className="player-app p-0 border-l w-full sm:max-w-sm"
          style={{ background: 'var(--vl-bg, #000000)', borderColor: 'rgba(0,77,97,0.3)' }}
          onPointerDownOutside={keepBottomNavTapForNavigation}
        >
          <SupportMenu
            branding={effectiveBranding}
            chatUnread={chatUnread}
            agentUnread={chatAgentUnread}
            attributedAgent={user?.agent_support || null}
            onPickAgent={(agent) => {
              moveSupportToChat(agent || null);
            }}
            onLiveChat={() => {
              moveSupportToChat(null);
            }}
            onClose={() => closeSupportMenu()}
          />
        </SheetContent>
      </Sheet>

      {/* Chat sheet */}
      <Sheet open={chatOpen} onOpenChange={(o) => { if (o) openChat(); else closeChat(); }}>
        <SheetContent
          side="right"
          className="player-app p-0 border-l w-full sm:max-w-md overflow-hidden overscroll-none"
          onPointerDownOutside={keepBottomNavTapForNavigation}
          style={{
            background: 'var(--vl-bg, #000000)',
            borderColor: 'rgba(0,77,97,0.3)',
            top: 'var(--player-chat-viewport-top, 0px)',
            bottom: 'auto',
            height: 'var(--player-chat-viewport-height, 100dvh)',
            maxHeight: 'var(--player-chat-viewport-height, 100dvh)',
            minHeight: 0,
            overscrollBehavior: 'none',
          }}
        >
          <ChatPanel
            active={chatOpen}
            user={user}
            selectedAgent={selectedChatAgent}
            onRead={() => fetchUnread()}
            onBackToSupport={() => {
              withOverlayHistorySuppressed(() => {
                setChatOpen(false);
                setSelectedChatAgent(null);
                setSupportMenuOpen(true);
                replaceCurrentOverlayHistory('support', null);
              });
            }}
          />
        </SheetContent>
      </Sheet>

      {/* Announcements panel */}
      <Sheet open={announcementsOpen} onOpenChange={(o) => { if (o) openAnnouncements(); else closeAnnouncements(); }}>
        <SheetContent
          side="right"
          className="player-app p-0 border-l w-full sm:max-w-md overflow-hidden"
          style={{ background: 'var(--vl-bg, #000000)', borderColor: 'rgba(255,217,61,0.35)' }}
        >
          <SheetHeader className="sr-only"><SheetTitle>Announcements</SheetTitle></SheetHeader>
          <AnnouncementsPanel close={() => closeAnnouncements()} />
        </SheetContent>
      </Sheet>

      {/* Notifications panel */}
      <Sheet open={notificationsOpen} onOpenChange={(o) => { if (o) openAlertsPanel(); else closeAlertsPanel(); }}>
        <SheetContent
          side="right"
          className="player-app p-0 border-l w-full sm:max-w-md overflow-hidden"
          style={{ background: 'var(--vl-bg, #000000)', borderColor: 'rgba(0,77,97,0.35)' }}
        >
          <SheetHeader className="sr-only"><SheetTitle>Notifications</SheetTitle></SheetHeader>
          <NotificationsPanel
            close={() => closeAlertsPanel()}
            detailOpen={notificationDetailOpen}
            detailItem={notificationDetailItem}
            onOpenDetail={openNotificationDetail}
            onCloseDetail={closeNotificationDetail}
            onRead={() => {
              setNotifUnread(0);
              toast.dismiss();
            }}
          />
        </SheetContent>
      </Sheet>

      {/* Achievements panel */}
      <Sheet open={achievementsOpen} onOpenChange={(o) => { if (o) openAchievements(); else closeAchievements(); }}>
        <SheetContent
          side="right"
          className="player-app p-0 border-l w-full sm:max-w-md overflow-hidden"
          style={{ background: 'var(--vl-bg, #000000)', borderColor: 'rgba(249,115,22,0.35)' }}
        >
          <SheetHeader className="sr-only"><SheetTitle>Achievements</SheetTitle></SheetHeader>
          <AchievementsPanel close={() => closeAchievements()} />
        </SheetContent>
      </Sheet>

      {/* Wallet */}
      {walletOpen && (
        <FullScreenWhitePanel onClose={goToLobby} testid="wallet-fs">
          <WalletFlow
            mode={walletMode}
            setMode={(m) => { setWalletMode(m); setWalletStep(1); setWalletMethod(null); }}
            step={walletStep}
            setStep={setWalletStep}
            method={walletMethod}
            setMethod={setWalletMethod}
            wallet={wallet}
            walletRules={walletRules}
            methods={data.methods}
            transactions={data.transactions}
            refresh={fetchData}
            checkAuth={checkAuth}
            close={goToLobby}
            branding={effectiveBranding}
            onOpenSupport={() => {
              closeOverlayWithBack('wallet', () => setWalletOpen(false));
              window.setTimeout(() => openSupportMenu(), 120);
            }}
          />
        </FullScreenWhitePanel>
      )}

      {/* Full-screen Profile */}
      {profileOpen && (
        <ProfileFullScreen
          user={user}
          branding={effectiveBranding}
          badge={badge}
          settingsOpen={profileSettingsOpen}
          onClose={() => closeProfile()}
          onOpenSettings={() => openProfileSettings({ restoreTo: 'profile' })}
          onCloseSettings={() => closeProfileSettings()}
          onEditProfile={() => openEditProfile({ restoreTo: 'profile' })}
          onEditPhone={() => openEditProfile({ restoreTo: 'profile', mode: 'phone-verify' })}
          onResetPassword={() => {
            withOverlayHistorySuppressed(() => {
              setProfileOpen(false);
              setTimeout(() => openResetPassword({ restoreTo: 'profile' }), 200);
            });
          }}
          onLogout={handleLogout}
        />
      )}

      {/* Balance details — modal showing wallet + total in games */}
      <Dialog open={balanceDetailOpen} onOpenChange={(o) => { if (o) openBalanceDetail(); else closeBalanceDetail(); }}>
        <DialogContent className="max-w-md p-0 border-0 overflow-hidden" style={{ background: '#fff' }} aria-describedby="balance-detail-desc">
          <DialogTitle className="sr-only">Balance Details</DialogTitle>
          <span id="balance-detail-desc" className="sr-only">View your wallet balance, freeplay credits, total game balance, and grand total.</span>
          <BalanceDetailPanel
            wallet={wallet}
            accounts={data.accounts}
            games={data.games}
            onPurchase={() => { closeBalanceDetail(); setTimeout(openPurchase, 200); }}
            onCashout={() => { closeBalanceDetail(); setTimeout(openCashout, 200); }}
            onExchangeFP={() => {
              tap();
              closeBalanceDetail();
              navigate(tenantSlug ? `/${tenantSlug}/app/explore/marketplace` : '/app/explore/marketplace');
            }}
            close={() => closeBalanceDetail()}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Profile dialog */}
      <Dialog open={editProfileOpen} onOpenChange={(o) => { if (o) openEditProfile(); else closeEditProfile(); }}>
        <DialogContent
          overlayClassName="!z-[80] !bg-black/70 backdrop-blur-lg"
          className="player-app max-w-md border z-[81] p-5 sm:p-6"
          onOpenAutoFocus={(event) => {
            if (editProfileMode === 'phone-verify') event.preventDefault();
          }}
          style={HALF_SHEET_DIALOG_STYLE}
        >
          <DialogHeader>
            <DialogTitle className="display text-xl font-extrabold" style={{ color: '#fff' }}>
              {editProfileMode === 'phone-verify' ? 'Verify Phone' : 'Edit Profile'}
            </DialogTitle>
          </DialogHeader>
          <EditProfilePanel
            user={user}
            checkAuth={checkAuth}
            close={() => closeEditProfile()}
            initialVerifyOpen={editProfileMode === 'phone-verify'}
          />
        </DialogContent>
      </Dialog>

      {/* Add Game dialog — full-screen */}
      <Dialog open={addGameOpen} onOpenChange={(o) => { if (o) openAddGame(); else closeAddGame(); }}>
        <DialogContent
          overlayClassName="!bg-black/60 backdrop-blur-md"
          className="player-app add-game-dialog border-0 p-6 sm:p-8 relative flex flex-col overflow-hidden"
          style={{
            background: 'var(--vl-bg, #000000)',
            color: 'var(--vl-text-primary, #FFFFFF)',
            boxShadow: '0 25px 60px rgba(0,0,0,0.8), 0 0 0 1px rgba(34,211,238,0.18)',
            position: 'fixed',
            top: 0,
            left: 0,
            transform: 'none',
            width: '100vw',
            height: '100dvh',
            maxWidth: '100vw',
            maxHeight: '100dvh',
            borderRadius: 0,
            margin: 0,
          }}
        >
          {/* Custom round cyan close button (slightly smaller) */}
          <button
            onClick={() => closeAddGame()}
            className="absolute right-3 top-3 rounded-full flex items-center justify-center transition active:scale-95 z-20 hover:scale-105"
            style={{
              width: '42px',
              height: '42px',
              background: 'rgba(34,211,238,0.12)',
              border: '2.5px solid #22D3EE',
              boxShadow: '0 0 0 3px rgba(34,211,238,0.18), 0 0 18px rgba(34,211,238,0.55), inset 0 0 12px rgba(34,211,238,0.18)'
            }}
            data-testid="add-game-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" style={{ color: '#22D3EE' }} strokeWidth={3} />
          </button>

          <DialogHeader className="items-center shrink-0">
            <DialogTitle className="display text-2xl sm:text-3xl font-extrabold text-center" style={{ color: '#FFFFFF' }}>Add a game</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 flex flex-col">
            <AddGamePanel
              games={data.games}
              accounts={myGameAccounts}
              refresh={fetchData}
              close={() => closeAddGame()}
              gameCreations={gameCreations}
              onStartCreate={startGameCreation}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Account Password dialog */}
      <Dialog open={resetPwOpen} onOpenChange={(o) => { if (o) openResetPassword(); else closeResetPassword(); }}>
        <DialogContent
          overlayClassName="!z-[80] !bg-black/55 backdrop-blur-md"
          className="player-app max-w-md border z-[81] p-5 sm:p-6"
          style={HALF_SHEET_DIALOG_STYLE}
        >
          <DialogHeader>
            <DialogTitle className="display text-xl font-extrabold" style={{ color: '#fff' }}>Reset Account Password</DialogTitle>
          </DialogHeader>
          <ResetPasswordPanel close={() => closeResetPassword()} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ExploreGiftArt() {
  return (
    <div
      className="explore-gift-art"
      role="img"
      aria-label="Gift rewards"
      data-testid="explore-earn-logo"
    >
      <img
        src={referAndEarnGiftCutout}
        alt=""
        aria-hidden="true"
        className="explore-gift-gif"
        loading="eager"
      />
    </div>
  );
}

function ProfileTrigger({ user, badge, onOpen }) {
  const avatarUrl = user?.avatar_url;
  const showBadge = !!badge?.show_badge;
  const tier = badge?.tier || {};
  return (
    <button
      onClick={onOpen}
      className="tap-pop-profile flex h-13 w-13 items-center justify-center rounded-full p-1 transition active:scale-[0.97]"
      style={{
        width: 52,
        height: 52,
        background: 'radial-gradient(circle at 32% 22%, rgba(255,255,255,0.28), rgba(56,189,248,0.18) 42%, rgba(10,18,26,0.88) 100%)',
        border: '1px solid rgba(56,189,248,0.45)',
        boxShadow: '0 10px 24px rgba(0,0,0,0.30), 0 0 18px rgba(56,189,248,0.24), inset 0 0 10px rgba(255,255,255,0.12)',
      }}
      data-testid="profile-menu-trigger"
      title={showBadge ? `${tier.label || 'Verified'} — phone verified` : 'Open profile'}
    >
      <span className="relative">
        <span
          className="w-11 h-11 rounded-full flex items-center justify-center overflow-hidden"
          style={{
            background: avatarUrl ? '#0B0F0C' : 'radial-gradient(circle at 30% 25%, #FFFFFF 0%, #38BDF8 35%, #0EA5E9 75%, #075985 100%)',
            boxShadow: '0 0 12px rgba(56,189,248,0.65), inset 0 0 8px rgba(255,255,255,0.45)',
            border: '2px solid #FFFFFF'
          }}
        >
          {avatarUrl
            ? <SafeImage src={avatarUrl} alt="avatar" className="w-full h-full object-cover" data-testid="profile-avatar-img" fallback={<CircleUser className="w-6 h-6" style={{ color: '#fff' }} />} />
            : <CircleUser className="w-6 h-6" style={{ color: '#fff' }} />}
        </span>
        {showBadge && (
          <span
            className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center"
            style={{
              background: 'radial-gradient(circle at 30% 25%, #FFF4B8 0%, var(--vl-brand-primary, #D4AF37) 48%, #8B5E00 100%)',
              border: '2px solid #0B0F0C',
              boxShadow: '0 0 8px color-mix(in oklab, var(--vl-brand-primary, #D4AF37) 72%, transparent), inset 0 1px 1px rgba(255,255,255,0.55)'
            }}
            data-testid="player-verified-badge"
            title={`${tier.label || 'Verified'} — phone verified`}
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 6L9 17l-5-5" stroke="#FFFFFF" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
        )}
      </span>
    </button>
  );
}


/* -------------------- BALANCE DETAIL PANEL -------------------- */
// Extracted to ./components/ProfilePanels.jsx (along with ResetPasswordPanel).



/* -------------------- CHAT PANEL -------------------- */
/* -------------------- HEADER ICON BUTTON (Announcements / Notifications / Achievements) -------------------- */
function HeaderIconBtn({ icon: Icon, color, title, onClick, badge, testid }) {
  return (
    <button
      onClick={onClick}
      title={title}
      aria-label={title}
      data-testid={testid}
      className="relative w-10 h-10 rounded-xl flex items-center justify-center transition active:scale-95 hover:brightness-110"
      style={{
        background: `linear-gradient(135deg, ${color}22, ${color}0A)`,
        border: `1px solid ${color}66`,
        boxShadow: `0 0 12px ${color}33, inset 0 1px 0 rgba(255,255,255,0.06)`,
      }}
    >
      <Icon className="w-5 h-5" style={{ color }} strokeWidth={2.3} />
      {badge > 0 && (
        <span
          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center display text-[10px] font-extrabold"
          style={{ background: '#EF4444', color: '#FFFFFF', border: '1.5px solid #0B130E' }}
          data-testid={`${testid}-badge`}
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );
}

/* -------------------- ANNOUNCEMENTS / NOTIFICATIONS / ACHIEVEMENTS -------------------- */
// Extracted to ./components/SidePanels.jsx — see imports at top of file.


/* -------------------- SOCIAL SUPPORT HALF-SCREEN OVERLAY -------------------- */
function SocialSupportOverlay({ branding, close }) {
  const socials = branding?.socials || {};
  const items = [
    { key: 'instagram', label: 'Instagram', handleDomain: 'instagram.com', gradient: 'linear-gradient(135deg, #004D61, #006B85 52%, #004D61 100%)', Icon: InstagramGlyph, cta: 'Follow' },
    { key: 'youtube',   label: 'YouTube',   handleDomain: 'youtube.com',   gradient: '#FF0000',                                                          Icon: YoutubeGlyph,   cta: 'Subscribe' },
    { key: 'tiktok',    label: 'TikTok',    handleDomain: 'tiktok.com',    gradient: '#000000',                                                          Icon: TikTokGlyph,    cta: 'Follow' },
    { key: 'facebook',  label: 'Facebook',  handleDomain: 'facebook.com',  gradient: '#1877F2',                                                          Icon: FacebookGlyph,  cta: 'Message' },
    { key: 'discord',   label: 'Discord',   handleDomain: 'discord.gg',    gradient: '#5865F2',                                                          Icon: DiscordGlyph,   cta: 'Join' },
    { key: 'telegram',  label: 'Telegram',  handleDomain: 't.me',          gradient: '#26A5E4',                                                          Icon: TelegramGlyph,  cta: 'Open' },
  ].filter(i => socials[i.key]);

  const emailRow = socials.support_email ? { href: `mailto:${socials.support_email}`, email: socials.support_email } : null;

  const open = (url) => window.open(url, '_blank', 'noopener,noreferrer');

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center px-4"
      style={{ background: 'rgba(3,5,12,0.55)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)' }}
      onClick={close}
      data-testid="social-support-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md max-h-[62vh] overflow-hidden rounded-t-3xl sm:rounded-3xl flex flex-col animate-fade-in-up"
        style={{
          background: 'var(--vl-bg, #000000)',
          border: '1px solid rgba(0,77,97,0.32)',
          boxShadow: '0 -14px 40px rgba(0,0,0,0.55), 0 0 40px rgba(0,77,97,0.16)'
        }}
      >
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b" style={{ borderColor: 'rgba(0,107,133,0.3)' }}>
          <div>
            <p className="display text-lg font-extrabold" style={{ color: '#fff' }}>Social Media Support</p>
            <p className="text-xs" style={{ color: '#C4B5FD' }}>Reach us on any of these channels</p>
          </div>
          <button onClick={close} className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.06)', color: '#fff' }} data-testid="social-support-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto p-4 space-y-2" data-testid="social-support-list">
          {items.length === 0 && !emailRow && (
            <div className="py-10 text-center">
              <Megaphone className="w-10 h-10 mx-auto mb-3 text-purple-300 opacity-60" />
              <p className="display font-bold text-white/80">No social channels configured</p>
              <p className="text-sm text-white/50 mt-1">An admin can add them in Branding.</p>
            </div>
          )}
          {items.map(item => {
            const url = socials[item.key];
            return (
              <button
                key={item.key}
                onClick={() => open(url)}
                className="w-full flex items-center gap-3 rounded-2xl p-3 transition active:scale-[0.99] hover:brightness-110"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
                data-testid={`social-support-${item.key}`}
              >
                <span className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: item.gradient, boxShadow: `0 4px 14px rgba(0,0,0,0.45)` }}>
                  <item.Icon />
                </span>
                <div className="flex-1 min-w-0 text-left">
                  <p className="font-bold text-white">{item.label}</p>
                  <p className="text-xs text-white/60 truncate">{url.replace(/^https?:\/\//, '')}</p>
                </div>
                <span className="shrink-0 display text-xs font-bold px-3 py-1.5 rounded-full" style={{ background: 'rgba(167,139,250,0.18)', color: '#E0F4F7', border: '1px solid rgba(167,139,250,0.5)' }}>{item.cta}</span>
              </button>
            );
          })}
          {emailRow && (
            <a href={emailRow.href} className="flex items-center gap-3 rounded-2xl p-3 transition active:scale-[0.99] hover:brightness-110"
              style={{ background: 'rgba(0,107,133,0.12)', border: '1px solid rgba(0,107,133,0.45)' }}
              data-testid="social-support-email">
              <span className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #006B85, #004D61)' }}>
                <Mail className="w-5 h-5 text-white" />
              </span>
              <div className="flex-1 min-w-0 text-left">
                <p className="font-bold text-white">Support Email</p>
                <p className="text-xs text-white/70 truncate">{emailRow.email}</p>
              </div>
              <span className="shrink-0 display text-xs font-bold px-3 py-1.5 rounded-full" style={{ background: 'rgba(167,139,250,0.25)', color: '#E0F4F7', border: '1px solid rgba(167,139,250,0.6)' }}>Email</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
